import { acceptHMRUpdate, defineStore } from 'pinia'
import { toRaw } from 'vue';
import { useMaps } from './maps'
import { useCustomers } from './customers'
import { useTasksStore } from './tasks'
import { useMeasure } from './measuring'
import * as turf from '@turf/turf'
import {v4 as uuid} from 'uuid'
import {
    postABLinesGen,
    postHeadlandGen,
    postGenerateABLinesOptions,
    postGenerateABLinesFromDB,
    postGenerateABLinesFromShape,
    postSaveFieldWithSurvey,
    postTrackSystemBorderArrayFromUpload,
    getDefaultHeadlandConfig,
    postTrackSystemCutoutsFromUpload,
    postExtractCurvedABlines,
    postSegmentHeadlands,
    postSegmentationInteractionPoints
} from '../scripts/fieldsPlanning'
import { updateField } from '../scripts/task'
import DeleteMenu from '../util/DeleteMenu'
import { useFieldProblemsStore } from './fieldProblems'
import { useSvgStore } from './svg'
import { getFieldDisplayName, useUploadMultipleBordersStore } from './uploadMultipleBorders'
import { useTextAreaStore } from './textArea'
import { useFieldSourceInfoStore } from './fieldSourseInfo'
import { useFieldLayersStore } from './fieldLayers'
import { useRoiStore } from './roi'
import { useUserRole } from './userRole'
import { useSegmentsStore } from './segments'
import { transformBordersToGeoJSON } from '../scripts/util/maps'
import { useSplitFieldStore } from './splitField'
import { stripClosingDuplicatePoint, geoJsonRingToOpenPath } from '@/helpers/geometryHelper'
import { removeFilenameExtension } from '@/helpers/stringHelper'
import { useImperialUnitsStore } from './imperialUnits'

export const MAX_TRACK_WIDTH = 35
export const MIN_TRACK_WIDTH = 13.5
export const MAX_HEADLAND_TRACKS = 8
export const MIN_HEADLAND_TRACKS = 1
export const DEFAULT_TRACK_WIDTH = 14
export const DEFAULT_HEADLAND_TRACKS_COUNT = 4

export const useDrawingStore = defineStore('drawing', {
    state: () => ({
        field: {
            id: null,
            name: '',
            trackWidth: DEFAULT_TRACK_WIDTH,
            headlandTracks: DEFAULT_HEADLAND_TRACKS_COUNT,
            headlands: Array(DEFAULT_HEADLAND_TRACKS_COUNT).fill(true).map((val, index) => index === 1 ? false : val),
            outerBorder: null,
            headlandResponse: null,
            requestHeadlandData: null,
            abLines: {
                longestEdge: null,
                fromManual: null,
                fromDBField: null,
                fromShape: null,
                parallel: null,
                originalABlines: null,
                angle: null,
                fromCoordinates: null,
                fromBorderPoints: null,
                curvedABlines: null
            },
            abLinesOptions: null,
            selectedABOption: null,
            configuration: false,
            farmDefaultConfig: null,
            farmDefaultTrackWidth: null,
            segments: [],
            shapes: [],
            cutouts: [],
            markers: {},
            changesOptions: [
                { name: 'Outer border', value: 'outer_border' },
                { name: 'Headland', value: 'headland' },
                { name: 'AB lines', value: 'ab_lines' }
            ]
        },
        selectedChangesOptions: ['outer_border', 'headland', 'ab_lines'], // Default contains all options
        drawingListener: null,
        isStartingDrawing: false,
        isDrawingObstacle: false,
        showDrawDirectionLine: false,
        showSelectBorder: false,
        showFieldInterior: false,
        drawedABlineCoordinates: [],
        parallelLineCoordinates: [],
        oldABLineCoordinates: [],
        headlandGenerated: false,
        ablinesGenerated: false,
        headlandPolygons: [], // Store directly created Google Maps Polygons for headlands {polygon, type}
        abLinesPolylines: [], // Store directly created Google Maps Polylines for AB lines (not in map.data)
        longestLinePolylines: [], // Store directly created Google Maps Polylines for longest AB lines (not in map.data)
        wheelTrackLinesPolylines: [], // Store directly created Google Maps Polylines for wheel track lines (not in map.data)
        steps: 0,
        selectedCustomer: null,
        selectedFieldFromDB: null,
        showFieldsList: false,
        uploadedFile: null,
        confirmedClear: false,
        loading: {
            abLines: false,
            dbFields: false,
            fileUpload: false,
            tableData: false,
            headland: false,
            longestEdge: false,
            saveField: false,
            prepareFieldForEdit: false
        },
        outerBorderDrawed: false,
        fieldValidationError: false,
        selectedItems: [],
        itemsUpload: [
            {
                label: 'Points on field border',
                icon: 'pi pi-share-alt',
                value: 'generateABLinesByBorderPoints',
            },
            /**
             * drawDirectionLine hidden for now (when reactivated, don't forget to uncomment the option from abLinesOptionsUpdate function)
             */
            // {
            //     label: 'Draw direction line',
            //     icon: 'pi pi-map-marker',
            //     value: 'drawDirectionLine'
            // },
            {
                label: 'Use direction from existing field',
                icon: 'pi pi-database',
                value: 'useDirectionFromExistingField'
            },
            {
                label: 'Import direction from file',
                icon: 'pi pi-file-import',
                value: 'importDirectionFromFile'
            },
            {
                label: 'Parallel to the headland line',
                icon: 'pi pi-pause',
                value: 'parallelToHeadlandLine',
            },
            {
                label: 'Generate AB lines by angle',
                icon: 'pi pi-angle-right',
                value: 'generateABLinesByAngle',
            },
            {
                label: 'Generate AB lines by coordinates',
                icon: 'pi pi-compass',
                value: 'generateABLinesByCoordinates',
            },
            {
                label: 'Import AB lines with curved lines',
                icon: 'fa-solid fa-s',
                value: 'importCurvedABlines'
            },
        ],
        deleteMenu: null,
        obstacleErrorType: null,
        isModified: false,
        obstacleWarningType: [],
        clickCountLine: 0,
        tempLine: null,
        mouseMoveListener: null,
        surveyOuterBorderFile: null,
        surveyABLineFile: null,
        surveyCurvedABlines: null,
        surveyCutoutsFiles: [],
        surveyCutoutsIDs: [],
        tempMarker: null,
        fieldsHidden: false,
        abLinesError: null,
        angleInput: null,
        coordinatesInput: {
            start: { lat: null, lng: null },
            end: { lat: null, lng: null }
        },
        fieldSaved: false,
        hasPaths: false,
        minAngle: 17,
        maxAngle: 18,
        actualSegments: [],
        showSelectBorderPoints: false,
        borderPointsStart: null,
        borderPointsEnd: null,
        borderPointsStartMarker: null,
        borderPointsEndMarker: null,
        borderPointsLine: null,
    }),
    getters: {
        hasValidFieldName: (state) => Boolean(state.field.name?.trim()),
    },
    actions: {
        handleContextMenu(shape) {
            const mapStore = useMaps()
            
            // Remove any existing listeners to prevent duplication
            mapStore.google.maps.event.clearListeners(shape, 'contextmenu')
            
            if (!this.deleteMenu) {
                this.deleteMenu = new DeleteMenu(toRaw(mapStore.map))
            }

            // Add the contextmenu listener
            mapStore.google.maps.event.addListener(shape, 'contextmenu', (e) => {
                
                if (e.vertex === undefined) {
                    this.deleteMenu.close()
                    return
                }
                
                // Get the path array for the current shape
                const path = shape.getPath()
                if (!path) {
                    console.error("Path invalid for shape:", shape)
                    return
                }
                
                // Open the delete menu
                this.deleteMenu.open({
                    shape: toRaw(shape),
                    vertex: e.vertex
                })
            })
        },
        nextStep() {
            this.steps++
            if (this.steps === 5) {
                this.centredMapOnField()
            }
        },
        async setCustomer() {
            const customersStore = useCustomers()
            if (customersStore.customers.length === 0) {
                await customersStore.getCustomers()
            }

            if (customersStore.customers.length === 1) {
                this.selectedCustomer = customersStore.customers[0]
            } else if (customersStore.selectedCustomers.length === 1) {
                this.selectedCustomer = customersStore.selectedCustomers[0]
            } else {
                this.selectedCustomer = null
            }
        },
        /** 
        * Convert TerraDraw GeoJSON to Google Maps overlay
        * TerraDraw uses GeoJSON format with coordinates as [lng, lat] arrays,
        * while Google Maps Polygon requires {lat, lng} objects.
        */
        convertTerraDrawFeatureToGoogleMapsOverlay(feature) {
            const mapStore = useMaps()
            const geometry = feature.geometry
            
            if (geometry.type === 'Polygon') {
                const paths = geoJsonRingToOpenPath(geometry.coordinates[0])

                // Rewind the path to have the hole inside the polygon
                const rewindedPaths = this.rewindRing(paths, true)
                const polygon = new mapStore.google.maps.Polygon({
                    paths: [rewindedPaths],
                    fillOpacity: 0.5,
                    strokeWeight: 2,
                    strokeColor: '#ff0000',
                    clickable: false,
                    editable: true,
                    zIndex: 1,
                })
                polygon.setMap(mapStore.map)
                return { overlay: polygon, type: 'polygon', path: rewindedPaths }
            } else if (geometry.type === 'LineString') {
                const path = geometry.coordinates.map(coord => ({
                    lat: coord[1],
                    lng: coord[0]
                }))
                const line = new mapStore.google.maps.Polyline({
                    path: path,
                    strokeWeight: 2,
                    strokeColor: '#ff0000',
                    clickable: false,
                    editable: true,
                    zIndex: 1000,
                })
                line.setMap(mapStore.map)
                return { overlay: line, type: 'line', path: path }
            } else if (geometry.type === 'Point') {
                const point = new mapStore.google.maps.marker.AdvancedMarkerElement({
                    position: { lat: geometry.coordinates[1], lng: geometry.coordinates[0] },
                    map: toRaw(mapStore.map),
                    zIndex: 1,
                })
                point.gmpDraggable = true
                return { 
                    overlay: point, 
                    type: 'point', 
                    path: [{ lat: geometry.coordinates[1], lng: geometry.coordinates[0] }] 
                }
            }
            return null
        },
        async setupDrawingListener() {
            const mapStore = useMaps()
            
            if (this.drawingListener) {
                this.cleanupDrawingListener()
            }
            
            // Cleanup ROI store listener to avoid conflicts
            const roiStore = useRoiStore()
            if (roiStore.terraDrawListener) {
                mapStore.terraDraw.off('finish', roiStore.terraDrawListener)
                roiStore.terraDrawListener = null
            }
            
            // TerraDraw uses 'finish' event when drawing is completed
            this.drawingListener = async (id) => {
                const features = mapStore.terraDraw.getSnapshot()
                const feature = features.find(f => f.id === id)
                if (feature) {
                    await this.handleDrawingComplete(feature)
                }
            }
            mapStore.terraDraw.on('finish', this.drawingListener)
        },
        cleanupDrawingListener() {
            const mapStore = useMaps()
            if (this.drawingListener) {
                // TerraDraw uses off() to remove listeners
                mapStore.terraDraw.off('finish', this.drawingListener)
                this.drawingListener = null
            }
        },
        async handleDrawingComplete(feature) {
            const converted = this.convertTerraDrawFeatureToGoogleMapsOverlay(feature)
            if (!converted) {
                await this.handleInvalidPolygon(null)
                return
            }

            const event = {
                overlay: converted.overlay,
                type: converted.type,
                feature: feature
            }
            const path = converted.path

            if (converted.type === 'polygon' && path.length > 2) {
                const success = await this.handlePolygonComplete(event, path)

                // Always remove TerraDraw feature after convert (same as ROI) —
                // otherwise select mode keeps black fill + vertex markers on top.
                this.removeTerraDrawFeature(feature)
                
                // Only restart TerraDraw if polygon was successfully processed
                if (success) {
                    const mapStore = useMaps()
                    if (mapStore.terraDraw) {
                        mapStore.terraDraw.start()
                        mapStore.terraDraw.setMode('select')
                    }
                }
            } else {
                await this.handleInvalidPolygon(event)
                this.removeTerraDrawFeature(feature)
            }
        },
        removeTerraDrawFeature(feature) {
            const mapStore = useMaps()
            if (!mapStore.terraDraw || !feature?.id) {
                return
            }
            
            const features = mapStore.terraDraw.getSnapshot()
            if (features.some(f => f.id === feature.id)) {
                mapStore.terraDraw.removeFeatures([feature.id])
            }
        },
        async handlePolygonComplete(event, path) {
            if (this.doEdgesIntersect(path)) {
                await this.handleInvalidPolygon(event)
                if (this.isDrawingObstacle) {
                    this.obstacleErrorType = 'edges_intersect'
                }
                return false
            }

            if (this.isDrawingObstacle) {
                const success = await this.handleObstacleDrawing(event.overlay)
                if (!success) {
                    event.overlay.setMap(null)
                    return false
                }
            } else {
                this.handleOuterBorderPolygon(event)
                await this.stopDrawing()
            }

            return true
        },
        doEdgesIntersect(path) {
            function doLinesIntersect(p1, p2, p3, p4) {
                const det = (p2.lng - p1.lng) * (p4.lat - p3.lat) - (p2.lat - p1.lat) * (p4.lng - p3.lng)
                if (det === 0) {
                    return false // Parallel lines
                }
                const lambda = ((p4.lat - p3.lat) * (p4.lng - p1.lng) + (p3.lng - p4.lng) * (p4.lat - p1.lat)) / det
                const gamma = ((p1.lat - p2.lat) * (p4.lng - p1.lng) + (p2.lng - p1.lng) * (p4.lat - p1.lat)) / det
                return (0 < lambda && lambda < 1) && (0 < gamma && gamma < 1)
            }

            for (let i = 0; i < path.length - 1; i++) {
                for (let j = i + 2; j < path.length - 1; j++) {
                    if (doLinesIntersect(path[i], path[i + 1], path[j], path[j + 1])) {
                        return true
                    }
                }
            }
            return false
        },
        isObstacleInsideField(obstacle) {
            const mapStore = useMaps()

            const fieldPolygon = this.field.shapes[0];
            const obstaclePath = obstacle.getPath().getArray();
            const fieldPath = fieldPolygon.getPath().getArray();

            const fieldPolygonObj = new mapStore.google.maps.Polygon({ paths: fieldPath });

            return obstaclePath.every(point => mapStore.google.maps.geometry.poly.containsLocation(point, fieldPolygonObj));
        },
        isObstacleInsideAnotherObstacle(obstacle, specificObstacle = null) {
            const mapStore = useMaps()

            const obstaclePath = obstacle.getPath().getArray()
            const obstaclesToCheck = specificObstacle ? [specificObstacle] : this.field.cutouts

            for (const existingObstacle of obstaclesToCheck) {
                const existingObstaclePath = toRaw(existingObstacle).getPath().getArray()
                const existingObstaclePolygon = new mapStore.google.maps.Polygon({ paths: existingObstaclePath })

                // Skip if it's the same obstacle
                if (obstacle === existingObstacle) {
                    continue
                }

                // Check if any point of the current obstacle is inside an existing obstacle
                if (obstaclePath.some(point => mapStore.google.maps.geometry.poly.containsLocation(point, existingObstaclePolygon))) {
                    return true
                }

                // Check if any point of the existing obstacle is inside the current obstacle
                if (existingObstaclePath.some(point => mapStore.google.maps.geometry.poly.containsLocation(point, new mapStore.google.maps.Polygon({ paths: obstaclePath })))) {
                    return true
                }

                // Check if any segment of the current obstacle intersects with any segment of the existing obstacle
                for (let j = 0; j < obstaclePath.length - 1; j++) {
                    for (let k = 0; k < existingObstaclePath.length - 1; k++) {
                        if (this.doEdgesIntersect([obstaclePath[j], obstaclePath[j + 1], existingObstaclePath[k], existingObstaclePath[k + 1]])) {
                            return true
                        }
                    }
                }
            }
            return false
        },
        async handleInvalidPolygon(event) {
            if (this.isDrawingObstacle) {
                event.overlay.setMap(null)
            } else {
                event.overlay.setMap(null)
                await this.clearDrawing()
            }
        },
        headlandTrackChange(newVal) {
            this.field.headlandTracks = newVal.value
            const currentLength = this.field.headlands.length
            
            if (newVal.value > currentLength) {
                // add new headland tracks
                const diff = newVal.value - currentLength
                this.field.headlands = [...this.field.headlands, ...Array(diff).fill(true)]
            } else {
                // reduce headland tracks
                this.field.headlands = this.field.headlands.slice(0, newVal.value)
            }
        },
        async startDrawing() {
            const mapStore = useMaps();
            const measureStore = useMeasure()
            this.isStartingDrawing = true;

            // Leftover measure overlays (Stop without Clear) steal the map visuals
            if (measureStore.measureBtnState) {
                measureStore.clearMeasuring(measureStore.buttonContainer)
            }
            
            // Start TerraDraw and set polygon mode
            if (!mapStore.terraDraw) {
                console.error('TerraDraw not initialized')
                return
            }
            
            mapStore.terraDraw.start()
            mapStore.terraDraw.setMode('polygon')
            
            if (this.deleteMenu) {
                this.deleteMenu.removeListener();
            }
            this.deleteMenu = new DeleteMenu(toRaw(mapStore.map));
            
            // Setup listener for drawing completion
            await this.setupDrawingListener()
            
            // Listen for context menu on polygons (handled after conversion to Google Maps overlay)
            const fieldSourceInfoStore = useFieldSourceInfoStore()
            fieldSourceInfoStore.setFieldSourceInfo('manual_drawn')
            
            mapStore.hideAllBorders()
            this.fieldsHidden = true
        },
        async toogleHeadlandConfig() {
            await this.fetchHeadlandConfig()
            this.field.configuration = await this.checkSameHeadlandConfig()
            await this.setDefaultConfiguration()
        },
        async fetchHeadlandConfig() {
            const selectedOrgId = this.selectedCustomer.id
            const response = await getDefaultHeadlandConfig(selectedOrgId)
            this.field.farmDefaultConfig = response[0].default_headland_config
            this.field.farmDefaultTrackWidth = response[0].default_track_width ?? DEFAULT_TRACK_WIDTH
            if (!this.field.farmDefaultConfig) {
                this.field.configuration = false
            }
        },
        getFarmDefaultTrackWidth() {
            return this.field.farmDefaultTrackWidth ?? DEFAULT_TRACK_WIDTH
        },
        async checkSameHeadlandConfig() {
            const tasksStore = useTasksStore()

            if (!this.field.farmDefaultConfig) {
                return false
            }

            if (this.field.id) {
                const farmTw = this.getFarmDefaultTrackWidth()
                const tw = tasksStore.field.track_width || farmTw
                const ht = tasksStore.field.config.length || DEFAULT_HEADLAND_TRACKS_COUNT
                const h = tasksStore.field.config
                    ? tasksStore.field.config.map((val) => val === 1.0)
                    : Array(DEFAULT_HEADLAND_TRACKS_COUNT)
                          .fill(true)
                          .map((val, index) => (index === 1 ? false : val))
                const farmDefaultConfigBoolean = this.field.farmDefaultConfig.map((val) => val === 1.0)
            
                return (
                    Number(tw) === Number(farmTw) &&
                    ht === this.field.farmDefaultConfig.length &&
                    h.every((val, index) => val === farmDefaultConfigBoolean[index]) &&
                    this.field.farmDefaultConfig.length === this.field.headlandTracks
                )

            } else {
                return this.field.farmDefaultConfig != null
            }
        },
        setConfig(trackWidth, headlandTracks, headlands) {
            this.field.trackWidth = trackWidth
            this.field.headlandTracks = headlandTracks
            this.field.headlands = headlands
        },
        async setDefaultConfiguration() {
            const tasksStore = useTasksStore()
            const imp = useImperialUnitsStore()
            const isSameConfig = await this.checkSameHeadlandConfig()
            const farmTw = this.getFarmDefaultTrackWidth()

            if (this.field.id) {
                if (this.field.configuration) {
                    if (isSameConfig) {
                        this.setConfig(farmTw, this.field.farmDefaultConfig.length, this.field.farmDefaultConfig.map(val => val === 1.0))
                    } else {
                        this.setConfig(
                            farmTw,
                            this.field.farmDefaultConfig.length ? this.field.farmDefaultConfig.length : DEFAULT_HEADLAND_TRACKS_COUNT,
                            this.field.farmDefaultConfig
                                ? this.field.farmDefaultConfig.map((val) => val === 1.0)
                                : Array(DEFAULT_HEADLAND_TRACKS_COUNT)
                                      .fill(true)
                                      .map((val, index) => (index === 1 ? false : val)),
                        )
                    }
                } else {
                    if (isSameConfig) {
                        this.setConfig(farmTw, this.field.farmDefaultConfig.length, this.field.farmDefaultConfig.map(val => val === 1.0))
                    } else {
                        this.setConfig(
                            tasksStore.field.track_width ? tasksStore.field.track_width : farmTw,
                            tasksStore.field.config.length ? tasksStore.field.config.length : DEFAULT_HEADLAND_TRACKS_COUNT,
                            tasksStore.field.config
                                ? tasksStore.field.config.map((val) => val === 1.0)
                                : Array(DEFAULT_HEADLAND_TRACKS_COUNT)
                                      .fill(true)
                                      .map((val, index) => (index === 1 ? false : val)),
                        )
                    }
                }
            } else {
                if (this.field.configuration) {
                    if (this.field.farmDefaultConfig != null) {
                        this.setConfig(farmTw, this.field.farmDefaultConfig.length, this.field.farmDefaultConfig.map(val => val === 1.0))
                    } else {
                        this.setConfig(farmTw, DEFAULT_HEADLAND_TRACKS_COUNT, Array(DEFAULT_HEADLAND_TRACKS_COUNT).fill(true).map((val, index) => index === 1 ? false : val))
                    }
                } else {
                    this.setConfig(
                        farmTw,
                        this.field.farmDefaultConfig ? this.field.farmDefaultConfig.length : DEFAULT_HEADLAND_TRACKS_COUNT,
                        this.field.farmDefaultConfig
                            ? this.field.farmDefaultConfig.map((val) => val === 1.0)
                            : Array(DEFAULT_HEADLAND_TRACKS_COUNT)
                                  .fill(true)
                                  .map((val, index) => (index === 1 ? false : val)),
                    )
                }
            }
            imp.syncSelectionFromMetricWidth(this.field.trackWidth)
        },
        startDrawingObstacles() {
            const mapStore = useMaps()

            this.clearABLinesMap()
            this.showFieldInterior = true
            this.setFieldStyle()
            this.isDrawingObstacle = true
            
            if (!mapStore.terraDraw) {
                console.error('TerraDraw not initialized')
                return
            }
            
            mapStore.terraDraw.start()
            mapStore.terraDraw.setMode('polygon')
            this.setupDrawingListener()
        },
        stopDrawingObstacles() {
            const mapStore = useMaps()

            if (mapStore.terraDraw) {
                mapStore.terraDraw.setMode('select')
                mapStore.terraDraw.stop()
            }
            this.isDrawingObstacle = false
            
            this.turnOnEditablePolygon()

            this.showFieldInterior = false
            this.setFieldStyle()
        },
        async stopDrawing() {
            const mapStore = useMaps()

            if (this.confirmedClear) {
                await this.clearDrawing()

                const tasksStore = useTasksStore()
                if (tasksStore.field) {
                    tasksStore.reset()
                }
                return
            }
            if (mapStore.terraDraw) {
                mapStore.terraDraw.setMode('select')
                this.handleContextMenu(this.field.shapes[this.field.shapes.length - 1])
                mapStore.terraDraw.stop()
            }
        },
        clearABLinesMap() {
            // Remove AB lines polylines
            this.abLinesPolylines.forEach(polyline => {
                toRaw(polyline).setMap(null)
            })
            this.abLinesPolylines = []

            // Remove longest line polylines
            this.longestLinePolylines.forEach(polyline => {
                toRaw(polyline).setMap(null)
            })
            this.longestLinePolylines = []

            // Remove wheel track lines polylines
            this.wheelTrackLinesPolylines.forEach(polyline => {
                toRaw(polyline).setMap(null)
            })
            this.wheelTrackLinesPolylines = []
        },
        /**
         * Add AB lines as Google Maps Polylines directly on the map (not in map.data)
         * This prevents TerraDraw from trying to style them when drawing split lines
         */
        addABLinesAsPolylines(geoJson) {
            const mapStore = useMaps()
            const fls = useFieldLayersStore()
            const abLinesColor = fls.getLayer('ab_lines').color || '#00ff00'

            if (!geoJson || !geoJson.features) {
                console.warn('Invalid GeoJSON for AB lines:', geoJson)
                return
            }

            geoJson.features.forEach(feature => {
                const geometry = feature.geometry
                
                if (geometry.type === 'MultiLineString') {
                    // MultiLineString contains multiple LineString arrays
                    geometry.coordinates.forEach(lineStringCoords => {
                        const path = lineStringCoords.map(coord => ({
                            lat: coord[1],
                            lng: coord[0]
                        }))
                        
                        const polyline = new mapStore.google.maps.Polyline({
                            path: path,
                            strokeColor: this.showFieldInterior ? 'transparent' : abLinesColor,
                            strokeOpacity: 1,
                            strokeWeight: 3,
                            geodesic: true,
                            clickable: false,
                            zIndex: 30,
                            map: toRaw(mapStore.map)
                        })
                        
                        this.abLinesPolylines.push(polyline)
                    })
                } else {
                    console.warn('Invalid geometry type for AB lines:', geometry.type)
                    return
                }
            })
        },
        /**
         * Add longest AB line as Google Maps Polyline directly on the map (not in map.data)
         * This prevents TerraDraw from trying to style it when drawing split lines
         */
        addLongestLineAsPolyline(geoJson) {
            const mapStore = useMaps()
            const longestLineColor = '#0000FF' // Blue color for longest line

            if (!geoJson || !geoJson.features || geoJson.features.length === 0) {
                console.warn('Invalid GeoJSON for longest line:', geoJson)
                return
            }

            const feature = geoJson.features[0]
            const geometry = feature.geometry
            
            if (geometry.type === 'LineString') {
                const path = geometry.coordinates.map(coord => ({
                    lat: coord[1],
                    lng: coord[0]
                }))
                
                const polyline = new mapStore.google.maps.Polyline({
                    path: path,
                    strokeColor: this.showFieldInterior ? 'transparent' : longestLineColor,
                    strokeOpacity: 1,
                    strokeWeight: 3,
                    geodesic: true,
                    clickable: false,
                    zIndex: 1000,
                    map: toRaw(mapStore.map)
                })
                
                this.longestLinePolylines.push(polyline)
            } else {
                console.warn('Invalid geometry type for longest line:', geometry.type)
                return
            }
        },
        /**
         * Add wheel track lines as Google Maps Polylines directly on the map (not in map.data)
         * This prevents TerraDraw from trying to style them when drawing split lines
         */
        addWheelTrackLinesAsPolylines(geoJson) {
            const mapStore = useMaps()
            const fls = useFieldLayersStore()
            
            // Check visibility from newLayers - default to hidden
            const trackWheelsLayer = fls.newLayers?.find(l => l.id === 'track_wheels')
            const isVisible = trackWheelsLayer?.visible === true && !this.showFieldInterior
            
            const wheelTrackColor = trackWheelsLayer?.color || '#FFFF00'

            if (!geoJson || !geoJson.features || geoJson.features.length === 0) {
                console.warn('Invalid GeoJSON for wheel track lines:', geoJson)
                return
            }

            const feature = geoJson.features[0]
            const geometry = feature.geometry
            
            if (geometry.type === 'MultiLineString') {
                geometry.coordinates.forEach(lineStringCoords => {
                    const path = lineStringCoords.map(coord => ({
                        lat: coord[1],
                        lng: coord[0]
                    }))
                    
                    const polyline = new mapStore.google.maps.Polyline({
                        path: path,
                        fillOpacity: 0,
                        strokeColor: wheelTrackColor,
                        strokeWeight: 2,
                        strokeOpacity: 0,
                        icons: [{
                            icon: {
                                path: 'M 0,0 0,1',
                                strokeOpacity: isVisible ? 1 : 0,
                                scale: 4
                            },
                            offset: '0',
                            repeat: '20px'
                        }],
                        geodesic: true,
                        clickable: false,
                        zIndex: 100,
                        map: toRaw(mapStore.map)
                    })
                    
                    this.wheelTrackLinesPolylines.push(polyline)
                })
            } else {
                console.warn('Invalid geometry type for wheel track lines:', geometry.type)
            }
        },
        async clearDrawing(fieldHasPaths = false) {
            const mapStore = useMaps()
            const imp = useImperialUnitsStore()

            this.cleanupDrawingListener()

            if (mapStore.terraDraw && mapStore.terraDraw.enabled) {
                mapStore.terraDraw.setMode('select')
                mapStore.terraDraw.stop()
            }

            // Reset all state flags
            this.isStartingDrawing = false
            this.confirmedClear = false
            this.outerBorderDrawed = false
            this.field.configuration = this.field.farmDefaultConfig ? true : false
            this.isDrawingObstacle = false
            this.steps = 0
            this.showSelectBorder = false
            this.showDrawDirectionLine = false
            this.isModified = false
            this.tempMarker = null
            this.fieldsHidden = false
            this.showSelectBorderPoints = false

            // Clear shapes
            this.field.shapes.forEach(shape => toRaw(shape).setMap(null))
            this.field.cutouts.forEach(cutout => toRaw(cutout).setMap(null))
            this.field.segments.forEach(segment => toRaw(segment).setMap(null))
            
            // Clear headland polygons
            this.headlandPolygons.forEach(item => toRaw(item.polygon).setMap(null))
            this.headlandPolygons = []

            // Clear AB lines polylines
            this.abLinesPolylines.forEach(polyline => toRaw(polyline).setMap(null))
            this.abLinesPolylines = []

            // Clear longest line polylines
            this.longestLinePolylines.forEach(polyline => toRaw(polyline).setMap(null))
            this.longestLinePolylines = []

            // Clear wheel track lines polylines
            this.wheelTrackLinesPolylines.forEach(polyline => toRaw(polyline).setMap(null))
            this.wheelTrackLinesPolylines = []
            
            // Clear all markers including obstacle labels
            Object.values(this.field.markers).forEach(marker => {
                toRaw(marker).setMap(null)
            })
            
            // Reset arrays
            this.field.cutouts = []
            this.field.shapes = []
            this.field.markers = {}
            this.field.segments = []
            this.parallelLineCoordinates = []
            this.drawedABlineCoordinates = []
            this.oldABLineCoordinates = []
            this.selectedItems = []
            this.obstacleWarningType = []
            this.surveyCutoutsFiles = []
            this.surveyCutoutsIDs = []
            this.removeBorderPointsListener(true)
            this.selectedChangesOptions = ['outer_border', 'headland', 'ab_lines'] // Re-add all options
            this.actualSegments = []

            // Reset other field properties
            this.field.id = null
            this.field.trackWidth = this.getFarmDefaultTrackWidth()
            
            // Reset headland configuration based on farmDefaultConfig if available
            if (this.field.farmDefaultConfig && this.field.farmDefaultConfig.length > 0) {
                this.field.headlandTracks = this.field.farmDefaultConfig.length
                this.field.headlands = this.field.farmDefaultConfig.map((val) => val === 1.0)
            } else {
                this.field.headlandTracks = DEFAULT_HEADLAND_TRACKS_COUNT
                this.field.headlands = Array(DEFAULT_HEADLAND_TRACKS_COUNT).fill(true).map((val, index) => (index === 1 ? false : val))
            }

            imp.syncSelectionFromMetricWidth(this.field.trackWidth)

            // Reset generation flags and responses
            this.uploadedFile = null
            this.headlandGenerated = false
            this.ablinesGenerated = false
            this.field.headlandResponse = null
            this.field.requestHeadlandData = null
            this.field.outerBorder = null
            this.field.abLines = {
                longestEdge: null,
                fromManual: null,
                fromDBField: null,
                fromShape: null,
                parallel: null,
                originalABlines: null,
                angle: null,
                fromCoordinates: null,
                fromBorderPoints: null,
                curvedABlines: null
            }
            this.field.abLinesOptions = null
            this.field.selectedABOption = null
            this.fieldValidationError = false
            this.selectedFieldFromDB = null
            this.obstacleErrorType = null
            if (this.surveyOuterBorderFile) {
                this.field.name = ''
            }
            this.surveyOuterBorderFile = null
            this.surveyABLineFile = null
            this.surveyCurvedABlines = null
            this.angleInput = null
            this.abLinesError = null
            this.coordinatesInput = {
                start: { lat: null, lng: null },
                end: { lat: null, lng: null }
            }

            // Reset loading states
            this.loading = {
                abLines: false,
                dbFields: false,
                fileUpload: false,
                tableData: false,
                headland: false,
                longestEdge: false,
                prepareFieldForEdit: false
            }

            // Show all borders after clearing
            await mapStore.showAllBorders()
            // Reset the hasSpecialABLines flag
            mapStore.draw.field.hasSpecialABLines = false

            // Clear all contextmenu listeners
            if (this.deleteMenu) {
                this.deleteMenu.removeListener()
                this.deleteMenu = null
            }

            // Remove contextmenu listeners from all shapes and obstacles
            [...this.field.shapes, ...this.field.cutouts].forEach(shape => {
                if (shape) {
                    mapStore.google.maps.event.clearListeners(shape, 'contextmenu')
                }
            })

            // Clear all markers from fieldProblemsStore
            const fieldProblemsStore = useFieldProblemsStore()
            fieldProblemsStore.clearMarkers()
            fieldProblemsStore.allIgnorableSkipped = false

            // Close the delete confirm modal
            const svgStore = useSvgStore()
            svgStore.setEditMode(false)

            // Reset field source info
            const fieldSourceInfoStore = useFieldSourceInfoStore()
            fieldSourceInfoStore.resetFieldSourceInfo()

            // Reset field layers
            await this.removeFieldLayers()

            // Clearing the hasPaths means the user is no longer editing a field
            if (!fieldHasPaths) {
                this.hasPaths = false
            }

            const seg = useSegmentsStore()
            seg.clearAll()

            const sf = useSplitFieldStore()
            sf.clearPreviousDrawing()
            sf.stopDrawingSplitLine()
            sf.isSplitting = false
        },
        async removeFieldLayers() {
            const fls = useFieldLayersStore()
            const mapStore = useMaps()

            fls.restoreOriginalSettings()
            await fls.checkRouteForVisibility()
            mapStore.hideNavField()
        },
        prepareHeadlandRequestData() {
            if (!this.field.shapes.length || !this.outerBorderDrawed) {
                return null
            }

            const coordinates = this.field.shapes[0].getPath().getArray()
                .map(point => [point.lng(), point.lat()])

            if (coordinates[0][0] !== coordinates[coordinates.length - 1][0] ||
                coordinates[0][1] !== coordinates[coordinates.length - 1][1]) {
                coordinates.push(coordinates[0])
            }

            this.field.outerBorder = {
                type: 'Polygon',
                coordinates: [coordinates]
            }

            const config = this.field.headlands
                .slice(0, this.field.headlandTracks)
                .map(value => value ? 1.0 : 0.5)

            const data = {
                config,
                track_width: this.field.trackWidth,
                outer_border: {
                    type: 'FeatureCollection',
                    features: [{
                        id: '0',
                        type: 'Feature',
                        properties: { field_geom_type: 'outer_border' },
                        geometry: {
                            type: 'Polygon',
                            coordinates: [
                                coordinates,
                                ...this.field.cutouts.map(cutout => {
                                    const ring = cutout.getPath().getArray()
                                        .map(coord => [coord.lng(), coord.lat()])
                                    if (ring.length > 0
                                        && (ring[0][0] !== ring[ring.length - 1][0]
                                            || ring[0][1] !== ring[ring.length - 1][1])) {
                                        ring.push(ring[0])
                                    }
                                    return ring
                                })
                            ]
                        }
                    }]
                }
            }

            this.field.requestHeadlandData = data
            return data
        },
        async generateHeadlands() {
            const mapStore = useMaps()
            this.loading.headland = true

            try {
                const data = this.prepareHeadlandRequestData()
                if (!data) {
                    throw new Error('No field shape found')
                }

                const response = await postHeadlandGen(data)

                let hasInnerBorder = false
                if (response && response.features) {
                    hasInnerBorder = response.features.some(feature => feature?.properties?.field_geom_type === 'inner_border')
                }

                if (!response || !hasInnerBorder) {
                    this.handleFieldTooSmallError('headlands')
                    throw new Error('No response from headland generation')
                }

                // clear headlands and inner borders if they exist
                if (this.headlandGenerated) {
                    // Remove directly created polygons
                    this.headlandPolygons.forEach(item => {
                        toRaw(item.polygon).setMap(null)
                    })
                    this.headlandPolygons = []
                }

                this.headlandGenerated = true
                this.abLinesError = null
                
                // Create polygons directly instead of using Data Layer to avoid TerraDraw interference
                response.features.forEach(feature => {
                    const fieldType = feature.properties?.field_geom_type || 'headland'
                    const isInnerBorder = fieldType === 'inner_border'
                    const strokeColor = isInnerBorder ? '#800080' : '#40E0D0' // turquoise for headland
                    
                    if (feature.geometry.type === 'Polygon') {
                        // Process Polygon: first ring is outer, rest are inner rings (holes)
                        const outerRing = feature.geometry.coordinates[0]
                        const innerRings = feature.geometry.coordinates.slice(1)
                        
                        const outerPath = outerRing.map(coord => ({
                            lat: coord[1],
                            lng: coord[0]
                        }))
                        
                        const paths = [outerPath]
                        // Add inner rings (holes) for obstacles
                        innerRings.forEach(innerRing => {
                            paths.push(innerRing.map(coord => ({
                                lat: coord[1],
                                lng: coord[0]
                            })))
                        })
                        
                        const polygon = new mapStore.google.maps.Polygon({
                            paths: paths,
                            strokeColor: this.showFieldInterior ? 'transparent' : strokeColor,
                            strokeOpacity: 1,
                            strokeWeight: 2,
                            fillColor: 'transparent',
                            fillOpacity: 0,
                            clickable: false,
                            map: toRaw(mapStore.map),
                            zIndex: 20,
                            visible: isInnerBorder, // Headland is hidden by default, inner_border is visible
                        })
                        
                        // Store polygon reference with type
                        this.headlandPolygons.push({ polygon, type: fieldType })
                    } else if (feature.geometry.type === 'MultiPolygon') {
                        // Process MultiPolygon: each element is a Polygon
                        feature.geometry.coordinates.forEach(polygonCoords => {
                            const outerRing = polygonCoords[0]
                            const innerRings = polygonCoords.slice(1)
                            
                            const outerPath = outerRing.map(coord => ({
                                lat: coord[1],
                                lng: coord[0]
                            }))
                            
                            const paths = [outerPath]
                            // Add inner rings (holes) for obstacles
                            innerRings.forEach(innerRing => {
                                paths.push(innerRing.map(coord => ({
                                    lat: coord[1],
                                    lng: coord[0]
                                })))
                            })
                            
                            const polygon = new mapStore.google.maps.Polygon({
                                paths: paths,
                                strokeColor: this.showFieldInterior ? 'transparent' : strokeColor,
                                strokeOpacity: 1,
                                strokeWeight: 2,
                                fillColor: 'transparent',
                                fillOpacity: 0,
                                clickable: false,
                                map: toRaw(mapStore.map),
                                zIndex: 20,
                                visible: isInnerBorder, // Headland is hidden by default, inner_border is visible
                            })
                            
                            // Store polygon reference with type
                            this.headlandPolygons.push({ polygon, type: fieldType })
                        })
                    }
                })

                this.field.headlandResponse = response

                await this.getSegmentedHeadlands(data.outer_border.features[0].geometry.coordinates)
                await this.checkSegmentation()

                this.abLinesOptionsUpdate()

                this.loading.headland = false
                return true
            } catch (error) {
                console.error('Headland generation failed:', error)
                this.loading.headland = false
                return false
            }
        },
        async setAngles() {
            await this.getSegmentedHeadlands(this.field.outerBorder.coordinates)
            await this.checkSegmentation()
        },
        async checkSegmentation() {
            const data = {
                headland_segments: this.actualSegments,
                angle_diff_lower_threshold: this.minAngle,
                angle_diff_upper_threshold: this.maxAngle
            }
            const response = await postSegmentationInteractionPoints(data)
            if (response === false) throw new Error('No response from segmentation interaction points')

            const seg = useSegmentsStore()
            const features = Array.isArray(response) ? response : response.features ?? []
            seg.interactionPointFeatures = features
            seg.applySegmentsPointsVisibility()
        },
        async getSegmentedHeadlands(outerBorderCoordinates) {
            const features = this.field.headlandResponse?.features ?? []
        
            // take only headlands (ignore inner_border)
            const headlandGeometries = features
                .filter(f => f.properties?.field_geom_type === 'headland')
                .map(f => ({
                    // all are Polygons, pack them as MultiPolygon
                    type: 'MultiPolygon',
                    coordinates: [f.geometry.coordinates]
                }))

                
            const data = {
                headland: {
                    type: 'GeometryCollection',
                    geometries: headlandGeometries
                },
                border: {
                    type: 'Polygon',
                    coordinates: outerBorderCoordinates
                },
                angle_diff_upper_threshold: this.maxAngle,
            }
            
            const response = await postSegmentHeadlands(data)

            const seg = useSegmentsStore()
            const wasShowingSegmentsPoints = seg.showSegmentsPoints
            seg.clearAll()
            seg.showSegmentsPoints = wasShowingSegmentsPoints
            seg.showAllSegmentsLines(response)
            this.actualSegments = response
        },
        shouldGenerateABLines() {
            const hasABLines = this.selectedChangesOptions.includes('ab_lines')
            if (hasABLines) {
                return true
            }
            return false
        },
        async generateABLines(type) {
            if (!this.shouldGenerateABLines()) {
                return
            }
            const mapStore = useMaps()

            this.clearABLinesMap()

            this.loading.abLines = true
            if (this.field.shapes.length === 0) {
                console.error('No shapes found')
                this.loading.abLines = false
                return
            }

            const data = {
                track_width: this.field.trackWidth.toFixed(1),
                headland: this.field.headlandResponse,
                outer_border: {
                    type: 'FeatureCollection',
                    features: [
                        {
                            id: '0',
                            type: 'Feature',
                            properties: {
                                field_geom_type: 'outer_border'
                            },
                            geometry: {
                                type: "Polygon",
                                coordinates: this.field.outerBorder.coordinates
                            }
                        }
                    ]
                },
            }

            // In edit mode, use the existing field ab_lines
            if (this.oldABLineCoordinates != null && type === 'originalABlines') {
                // If the field has special AB lines (curved AB lines), we need to send the field_id
                if (mapStore.draw.field.hasSpecialABLines) {
                    data.field_id = this.field.id
                } else {
                    // If the field does not have special AB lines, we need to send the reference (first AB line)
                    data.reference = {
                        type: 'FeatureCollection',
                        features: [
                            {
                                type: 'Feature',
                                properties: {},
                                geometry: {
                                    coordinates: Array.isArray(this.oldABLineCoordinates[0]) 
                                        ? this.oldABLineCoordinates 
                                        : this.oldABLineCoordinates.map(point => [point.lng(), point.lat()]),
                                    type: 'LineString'
                                }
                            }
                        ]
                    }
                }
            }

            // If manual or parallel line coordinates are used, add them to the data object
            if ((this.drawedABlineCoordinates.length === 2 && type === 'drawDirectionLine') || (this.parallelLineCoordinates.length === 2 && type === 'drawParallelLine')) {
                data.reference = {
                    type: 'FeatureCollection',
                    features: [
                        {
                            type: 'Feature',
                            properties: {},
                            geometry: {
                                coordinates: (type === 'drawDirectionLine' ? this.drawedABlineCoordinates : this.parallelLineCoordinates)
                                    .map(point => [point.lng(), point.lat()]),
                                type: 'LineString'
                            }
                        }
                    ]
                }
            } else {
                if (this.drawedABlineCoordinates.length != 2) {
                    this.drawedABlineCoordinates = []
                }
            }

            if (type === 'angle' && this.angleInput !== null) {
                data.reference = this.angleInput
            }

            if (type === 'fromCoordinates') {
                data.reference = {
                    type: 'FeatureCollection',
                    features: [
                        {
                            type: 'Feature',
                            properties: {},
                            geometry: {
                                coordinates: [
                                    [this.coordinatesInput.start.lng, this.coordinatesInput.start.lat],
                                    [this.coordinatesInput.end.lng, this.coordinatesInput.end.lat]
                                ],
                                type: 'LineString'
                            }
                        }
                    ]
                }
            }

            if (type === 'fromBorderPoints' && this.borderPointsStart && this.borderPointsEnd) {
                data.reference = {
                    type: 'FeatureCollection',
                    features: [
                        {
                            type: 'Feature',
                            properties: {},
                            geometry: {
                                coordinates: [
                                    [this.borderPointsStart.lng, this.borderPointsStart.lat],
                                    [this.borderPointsEnd.lng, this.borderPointsEnd.lat]
                                ],
                                type: 'LineString'
                            }
                        }
                    ]
                }
            }

            this.turnOffEditablePolygon()

            try {
                let response = await postABLinesGen(data)
                if (!response) {
                    console.error('Failed to generate AB lines: No response received' + (type ? ' for ' + type : ''))

                    // It was not possible to generate the original AB lines
                    if (type === 'originalABlines') {
                        this.field.abLines.originalABlines = null
                        this.abLinesError = 'originalABlines'
                    }

                    // It was not possible to generate the manual line
                    if (type === 'drawDirectionLine') {
                        this.drawedABlineCoordinates = []
                        this.field.abLines.fromManual = null
                        this.abLinesError = 'drawDirectionLine'
                    }

                    // It was not possible to generate the parallel line
                    if (type === 'drawParallelLine') {
                        this.parallelLineCoordinates = []
                        this.field.abLines.parallel = null
                        this.abLinesError = 'drawParallelLine'
                    }

                    // It was not possible to generate the AB lines by coordinates
                    if (type === 'fromCoordinates') {
                        this.coordinatesInput = {
                            start: { lat: null, lng: null },
                            end: { lat: null, lng: null }
                        }
                        this.abLinesError = 'fromCoordinates'
                        this.field.abLines.fromCoordinates = null
                    }

                    if (type === 'fromBorderPoints') {
                        this.removeBorderPointsListener(true)
                        this.showSelectBorderPoints = false
                        this.abLinesError = 'fromBorderPoints'
                        this.field.abLines.fromBorderPoints = null
                    }

                    this.abLinesOptionsUpdate()

                    return false
                }

                this.ablinesGenerated = response?.ab_lines?.features?.length > 0
                if (this.abLinesError === type || this.abLinesError === 'fieldTooSmallForABLines') {
                    this.abLinesError = null
                }

                response.ab_lines.features[0].id = this.field.headlandResponse.features.length

                /**
                 * Only if we are not on edit mode, we need to add on the map the new lines
                 * Add as Polylines directly on map (not in map.data) to avoid TerraDraw conflicts
                 */
                if (this.field.id && this.field.abLines.originalABlines) {
                    this.field.selectedABOption = 'original_ab_lines'
                    this.addABLinesAsPolylines(this.field.abLines.originalABlines.ab_lines)
                } else {
                    this.field.selectedABOption = null
                    this.addABLinesAsPolylines(response.ab_lines)
                }

                if (this.drawedABlineCoordinates.length === 2 && type === 'drawDirectionLine') {
                    this.field.abLines.fromManual = response
                    this.abLinesOptionsUpdate()

                } else if (this.parallelLineCoordinates.length === 2 && type === 'drawParallelLine') {
                    this.field.abLines.parallel = response
                    this.abLinesOptionsUpdate()
                    this.showSelectBorder = true
                    await this.toggleSelectBorder()
                } else if (this.oldABLineCoordinates != null && type === 'originalABlines') {
                    this.field.abLines.originalABlines = response
                    this.abLinesOptionsUpdate()

                    if (this.steps === 4 && this.field.selectedABOption) {
                        this.showABLines()
                    } else {
                        this.field.selectedABOption = 'original_ab_lines'
                    }
                } else if (type === 'angle') {
                    this.field.abLines.angle = response
                    this.abLinesOptionsUpdate()
                } else if (type === 'fromCoordinates') {
                    this.field.abLines.fromCoordinates = response
                    this.abLinesOptionsUpdate()
                } else if (type === 'fromBorderPoints') {
                    this.field.abLines.fromBorderPoints = response
                    this.abLinesOptionsUpdate()
                }
                this.setFieldStyle()
                return true
            } catch (error) {
                console.error('There was a problem with the fetch operation:', error)
            } finally {
                this.loading.abLines = false
                // Restore polygon editing unless an AB line drawing mode is still active
                if (!this.showSelectBorderPoints && !this.showSelectBorder && !this.showDrawDirectionLine) {
                    this.turnOnEditablePolygon()
                }
            }
        },
        hasAnyValidABLinesOption(options) {
            if (!options) return false

            return Object.entries(options).some(([key, option]) => {
                if (key === 'generator_version') return false // Ignore generator version
                if (!option || typeof option !== 'object') return false // Ignore invalid options
                return option?.ab_lines?.features?.length > 0
            })
        },
        hasValidCustomABLinesInField() {
            const customResponses = [
                this.field.abLines.fromBorderPoints,
                this.field.abLines.fromDBField,
                this.field.abLines.fromShape,
                this.field.abLines.parallel,
                this.field.abLines.originalABlines,
                this.field.abLines.angle,
                this.field.abLines.fromCoordinates,
                this.field.abLines.fromManual,
                this.field.abLines.curvedABlines,
            ]

            return customResponses.some(response => response?.ab_lines?.features?.length > 0)
        },
        abLinesOptionsUpdate() {
            this.loading.tableData = true

            const updateOptions = (response, key) => {
                this.field.abLinesOptions = {
                    ...this.field.abLinesOptions,
                    [key]: {
                        lines_count: response?.lines_count || 0,
                        distance: response?.distance || 0,
                        ab_lines: response?.ab_lines || [],
                        longest_line: response?.longest_line || [],
                        longest_line_length: response?.longest_line_length || 0,
                        longest_line_coverage: response?.longest_line_coverage || 0,
                        wheel_track_lines: response?.wheel_track_lines || null,
                        ab_orientation_angle: response?.ab_orientation_angle ?? null,
                    }
                }
            }

            try {
                /**
                 * drawn_line hidden for now (when reactivated, don't forget to uncomment the option from state itemsUpload[1])
                 */
                // updateOptions(this.field.abLines.fromManual ? this.field.abLines.fromManual : null, 'drawn_line')
                updateOptions(this.field.abLines.fromBorderPoints ? this.field.abLines.fromBorderPoints : null, 'points_on_border')
                updateOptions(this.field.abLines.fromDBField ? this.field.abLines.fromDBField : null, 'selected_field')
                updateOptions(this.field.abLines.fromShape ? this.field.abLines.fromShape : null, 'uploaded_file')
                updateOptions(this.field.abLines.parallel ? this.field.abLines.parallel : null, 'parallel_line')
                updateOptions(this.field.abLines.originalABlines ? this.field.abLines.originalABlines : null, 'original_ab_lines')
                updateOptions(this.field.abLines.angle ? this.field.abLines.angle : null, 'angle')
                updateOptions(this.field.abLines.fromCoordinates ? this.field.abLines.fromCoordinates : null, 'from_coordinates')

                if (this.hasAdminAccess()) {
                    updateOptions(this.field.abLines.curvedABlines ? this.field.abLines.curvedABlines : null, 'curved_ab_lines')
                }

                this.showABLines()
            } finally {
                this.loading.tableData = false
            }
        },
        applyParallelLineSelectionStyle() {
            // Field outer border
            this.field.shapes.forEach(shape => {
                shape.setOptions({
                    fillColor: "#000000",
                    fillOpacity: 0.2,
                    strokeColor: "#000000",
                    strokeWeight: 2,
                })
            })

            // Field cutouts border
            this.field.cutouts.forEach(cutout => {
                cutout.setOptions({
                    fillColor: "#000000",
                    fillOpacity: 0,
                    strokeColor: "#000000",
                    strokeWeight: 2,
                })
            })

            // Headland and inner border polygons
            this.headlandPolygons.forEach(item => {
                toRaw(item.polygon).setOptions({
                    strokeColor: '#000000',
                    strokeOpacity: 1,
                    strokeWeight: 2,
                    fillColor: 'transparent',
                    fillOpacity: 0,
                    zIndex: 20
                })
            })
        },
        drawLongestABLine(option) {
            if (this.field.abLinesOptions[option] && this.field.abLinesOptions[option].longest_line) {
                const longestLine = this.field.abLinesOptions[option].longest_line
        
                // Set unique ID to avoid conflicts (without id has conflict with wheel track lines)
                longestLine.features[0].id = uuid()
                // Update the field_geom_type property to apply the correct style
                longestLine.features[0].properties.field_geom_type = 'longest_ab_line'
        
                // Add the longest line as Polyline directly on map (not in map.data) to avoid TerraDraw conflicts
                this.addLongestLineAsPolyline(longestLine)
            } else {
                console.error(`Option ${option} does not exist or does not have a longest line`)
            }
        },
        drawWheelTrackLines(option) {
            if (this.field.abLinesOptions[option] && this.field.abLinesOptions[option].wheel_track_lines) {
                const wheelTrackLines = this.field.abLinesOptions[option].wheel_track_lines
                
                // Set unique ID to avoid conflicts (without id has conflict with longest ab line)
                wheelTrackLines.features[0].id = uuid()
                // Update the field_geom_type property to apply the correct style
                wheelTrackLines.features[0].properties.field_geom_type = 'wheel_track_lines'
                
                // Add the wheel track lines as Polylines directly on map (not in map.data) to avoid TerraDraw conflicts
                this.addWheelTrackLinesAsPolylines(wheelTrackLines)
            } else {
                console.error(`Option ${option} does not exist or does not have wheel track lines`)
            }
        },
        setFieldStyle() {
            const fls = useFieldLayersStore()

            this.field.shapes.forEach(shape => {
                shape.setOptions({
                    fillColor: "#FF0000",
                    fillOpacity: 0.1,
                    strokeColor: "#FF0000",
                    strokeWeight: 2,
                    zIndex: 10
                })
            })

            this.field.cutouts.forEach(cutout => {
                cutout.setOptions({
                    fillColor: "#FF0000",
                    fillOpacity: 0,
                    strokeColor: "#FF0000",
                    strokeWeight: 2,
                })
            })
            
            // Update directly created headland polygons (not in Data Layer)
            const headlandColor = fls.getLayer('headland').color || '#40E0D0'
            this.headlandPolygons.forEach(item => {
                const isInnerBorder = item.type === 'inner_border'
                // inner border is purple, headland is turquoise
                const strokeColor = isInnerBorder ? '#800080' : headlandColor
                
                toRaw(item.polygon).setOptions({
                    strokeColor: this.showFieldInterior ? 'transparent' : strokeColor,
                    strokeOpacity: 1,
                    strokeWeight: 2,
                    fillColor: 'transparent',
                    fillOpacity: 0,
                    zIndex: 5
                })
            })

            const abLinesColor = fls.getLayer('ab_lines').color || '#00ff00'
            // Update directly created AB lines polylines (not in Data Layer)
            this.abLinesPolylines.forEach(polyline => {
                toRaw(polyline).setOptions({
                    strokeColor: this.showFieldInterior ? 'transparent' : abLinesColor,
                    strokeOpacity: 1,
                    strokeWeight: 3,
                    zIndex: 30
                })
            })

            // Update directly created longest line polylines (not in Data Layer)
            const longestLineColor = '#0000FF' // Blue color for longest line
            this.longestLinePolylines.forEach(polyline => {
                toRaw(polyline).setOptions({
                    strokeColor: this.showFieldInterior ? 'transparent' : longestLineColor,
                    strokeOpacity: 1,
                    strokeWeight: 3,
                    zIndex: 1000
                })
            })

            // Update directly created wheel track lines polylines (not in Data Layer)
            const trackWheelsLayer =
                fls.newLayers?.find((l) => l.id === 'track_wheels') ??
                fls.layers.find((l) => l.id === 'track_wheels')
            const wheelTrackColor = trackWheelsLayer?.color || '#FFFF00'
            const wheelIconsVisible = trackWheelsLayer?.visible === true && !this.showFieldInterior

            this.wheelTrackLinesPolylines.forEach(polyline => {
                toRaw(polyline).setOptions({
                    fillOpacity: 0,
                    strokeColor: this.showFieldInterior ? 'transparent' : wheelTrackColor,
                    strokeWeight: 2,
                    strokeOpacity: 0,
                    icons: [{
                        icon: {
                            path: 'M 0,0 0,1',
                            strokeOpacity: wheelIconsVisible ? 1 : 0,
                            scale: 4
                        },
                        offset: '0',
                        repeat: '20px'
                    }],
                    zIndex: 100
                })
            })
        },
        async drawDirectionLine() {
            this.showDrawDirectionLine = !this.showDrawDirectionLine

            if (this.field.id === null) { 
                this.field.selectedABOption = null
            } else {
                this.field.selectedABOption = 'original_ab_lines'
            }
            this.clearABLinesMap()

            if (this.showDrawDirectionLine) {
                if (this.showSelectBorder) {
                    await this.toggleSelectBorder()
                }
                if (this.showSelectBorderPoints) {
                    await this.toggleSelectBorderPoints()
                }
                this.turnOffEditablePolygon()
                this.startDrawingLine()
            } else {
                this.clearTempDrawing()
            }
            return this.showDrawDirectionLine
        },
        startDrawingLine() {
            const mapStore = useMaps()
            const measureStore = useMeasure()

            if (mapStore.terraDraw?.enabled) {
                mapStore.terraDraw.setMode('select')
                mapStore.terraDraw.stop()
            }
    
            this.drawedABlineCoordinates = []
            this.tempLine = null
            this.clickCountLine = 0

            measureStore.disableDrawedField()
            measureStore.setFieldsNotClickable()
            mapStore.map.setOptions({ draggableCursor: 'crosshair' })

            this.tempLine = new mapStore.google.maps.Polyline({
                path: this.drawedABlineCoordinates,
                strokeColor: '#00ff00',
                strokeOpacity: 1.0,
                strokeWeight: 3,
                map: toRaw(mapStore.map),
                clickable: false,
                zIndex: 1001
            })
    
            this.clickListener = mapStore.google.maps.event.addListener(mapStore.map, 'click', async (event) => {
                this.drawedABlineCoordinates.push(event.latLng)
                this.tempLine.setPath(this.drawedABlineCoordinates)
    
                this.clickCountLine++
    
                if (this.clickCountLine === 2) {
                    mapStore.google.maps.event.removeListener(this.clickListener)
                    mapStore.google.maps.event.removeListener(this.mouseMoveListener)
    
                    this.showDrawDirectionLine = false
                    await this.generateABLines('drawDirectionLine')
                    this.clearTempDrawing()
                }
            })
    
            this.mouseMoveListener = mapStore.google.maps.event.addListener(mapStore.map, 'mousemove', (event) => {
                if (this.clickCountLine === 1) {
                    const previewPath = [...this.drawedABlineCoordinates, event.latLng]
                    this.tempLine.setPath(previewPath)
                }
            })
        },
        clearTempDrawing() {
            const mapStore = useMaps()
            const measureStore = useMeasure()

            mapStore.map.setOptions({ draggableCursor: null })
            this.showDrawDirectionLine = false
        
            if (this.clickListener) {
                mapStore.google.maps.event.removeListener(this.clickListener)
                this.clickListener = null
            }

            if (this.tempLine) {
                toRaw(this.tempLine).setMap(null)
                this.tempLine = null
            }

            if (this.mouseMoveListener) {
                mapStore.google.maps.event.removeListener(this.mouseMoveListener)
                this.mouseMoveListener = null
            }

            this.clickCountLine = 0
            measureStore.enableDrawedField()
            measureStore.setFieldsClickable()

            if (!this.showSelectBorder && !this.showSelectBorderPoints) {
                this.turnOnEditablePolygon()
            }
        },
        async useDirectionFromExistingField(show = true) {
            this.loading.dbFields = true
            this.clearABLinesMap()
            this.turnOffEditablePolygon()

            try {
                if (!this.selectedFieldFromDB.id) {
                    throw new Error('No field selected')
                }

                const data = {
                    field_id: this.selectedFieldFromDB.id,
                    headland: this.field.headlandResponse,
                    outer_border: {
                        type: 'FeatureCollection',
                        features: [{
                            id: '0',
                            type: 'Feature',
                            properties: {
                                field_geom_type: 'outer_border'
                            },
                            geometry: {
                                type: "Polygon",
                                coordinates: this.field.outerBorder.coordinates
                            }
                        }],
                    },
                    track_width: this.field.trackWidth
                }

                const response = await postGenerateABLinesFromDB(data)
                
                if (!response) {
                    this.abLinesError = 'fromDBField'
                    throw new Error('Failed to generate AB lines from field in database!')
                }

                // process response
                response.ab_lines.features[0].id = this.field.headlandResponse.features.length
                this.ablinesGenerated = true
                
                // add the new lines to map only if we are not in edit mode
                // Add as Polylines directly on map (not in map.data) to avoid TerraDraw conflicts
                if (!this.field.id) {
                    if (show) {
                        this.addABLinesAsPolylines(response.ab_lines)
                    }
                } else {
                    if (this.steps === 4) {
                        this.showABLines()
                    } else {
                        this.addABLinesAsPolylines(this.field.abLines.originalABlines.ab_lines)
                    }
                }
                // update store
                this.field.abLines.fromDBField = response
                this.abLinesOptionsUpdate()
                this.setFieldStyle()
                this.turnOnEditablePolygon()

                return true
            } catch (error) {
                console.error('There was a problem with the fetch operation:', error)
                this.selectedFieldFromDB = null
                this.field.abLines.fromDBField = null
                this.abLinesOptionsUpdate()
                this.turnOnEditablePolygon()
                return false
            } finally {
                this.loading.dbFields = false
            }
        },
        async uploadFile(show = true, isCurved = false) {
            if (!this.uploadedFile) {
                return false
            }

            this.clearABLinesMap()

            const formData = new FormData()
            formData.append('file', this.uploadedFile)
            formData.append('track_width', this.field.trackWidth.toFixed(1))
            formData.append('headland', JSON.stringify(this.field.headlandResponse))
            formData.append('outer_border', JSON.stringify({
                type: 'FeatureCollection',
                features: [
                    {
                        id: '0',
                        type: 'Feature',
                        properties: {
                            field_geom_type: 'outer_border'
                        },
                        geometry: {
                            type: "Polygon",
                            coordinates: this.field.outerBorder.coordinates
                        }
                    }
                ]
            }))

            this.turnOffEditablePolygon()

            try {
                this.loading.fileUpload = true
                const response = isCurved ? await postExtractCurvedABlines(formData) : await postGenerateABLinesFromShape(formData)

                if (!response) {
                    this.loading.fileUpload = false
                    this.abLinesOptionsUpdate()
                    if (isCurved) {
                        this.abLinesError = 'fromShapeCurved'
                        this.field.abLines.curvedABlines = null
                    } else {
                        this.abLinesError = 'fromShape'
                        this.field.abLines.fromShape = null
                    }
                    throw new Error('Failed to generate AB lines from shape!')
                }

                response.ab_lines.features[0].id = this.field.headlandResponse.features.length

                if (isCurved) {
                    this.field.abLines.curvedABlines = response
                    this.surveyCurvedABlines = this.uploadedFile
                } else {
                    this.field.abLines.fromShape = response
                    this.surveyABLineFile = this.uploadedFile
                }

                this.abLinesOptionsUpdate()
                this.ablinesGenerated = true

                // add the new lines to map only if we are not in edit mode
                // Add as Polylines directly on map (not in map.data) to avoid TerraDraw conflicts
                if (!this.field.id) {
                    if (show) {
                        this.field.selectedABOption = null
                        this.addABLinesAsPolylines(response.ab_lines)
                    }
                } else {
                    this.addABLinesAsPolylines(this.field.abLines.originalABlines.ab_lines)   
                }

                this.setFieldStyle()
                this.loading.fileUpload = false
                this.turnOnEditablePolygon()

                return true
            } catch (error) {
                console.error('There was a problem with the fetch operation:', error)
                this.uploadedFile = null
                this.loading.fileUpload = false
                if (isCurved) {
                    this.surveyCurvedABlines = null
                } else {
                    this.surveyABLineFile = null
                }
                this.turnOnEditablePolygon()
                return false
            }
        },
        setFieldOwner(farmId) {
            const customersStore = useCustomers()
        
            if (customersStore.customers.length > 0) {
                const foundCustomer = customersStore.customers.find(customer => customer.id === farmId)
                if (foundCustomer) {
                    this.selectedCustomer = foundCustomer
                    customersStore.selectedCustomers = [foundCustomer]
                } else {
                    this.selectedCustomer = null
                    customersStore.selectedCustomers = []
                    console.error(`No customer found with id ${farmId}`)
                }
            } else {
                this.selectedCustomer = null
                customersStore.selectedCustomers = []
                console.error('No customers in list.')
            }
        },
        async editField(field, fromTrimble = false) {
            const mapStore = useMaps()

            try {
                this.clearABLinesMap()

                // Set shapes with the first line of the field
                let polygon = []
                if (field.outer_border && field.outer_border.coordinates.length > 0) {
                    const path = geoJsonRingToOpenPath(field.outer_border.coordinates[0])
                    polygon = new mapStore.google.maps.Polygon({
                        paths: path,
                        editable: true,
                        strokeColor: 'red',
                        strokeWeight: 2,
                        fillColor: 'transparent',
                        fillOpacity: 0,
                        zIndex: 1
                    })
                    this.field.shapes = [polygon]
                    polygon.setMap(mapStore.map)
                    this.handleContextMenu(polygon);
                    this.addHandleUpdateListeners(polygon)
                }

                // Add obstacles to cutouts array
                this.field.cutouts = [];
                if (field.outer_border && field.outer_border.coordinates.length > 1) {
                    for (let i = 1; i < field.outer_border.coordinates.length; i++) {
                        const cutoutPath = geoJsonRingToOpenPath(field.outer_border.coordinates[i]);
                        const cutoutPolygon = new mapStore.google.maps.Polygon({
                            id: uuid(),
                            paths: cutoutPath,
                            editable: true,
                            strokeColor: 'red',
                            strokeWeight: 2,
                            fillColor: 'transparent',
                            fillOpacity: 0,
                            zIndex: 1
                        })
                        this.field.cutouts.push(cutoutPolygon)
                        cutoutPolygon.setMap(mapStore.map)
                        this.addHandleUpdateListeners(cutoutPolygon)
                    }
                }

                // Set other field data
                this.field.name = field.field_name
                this.field.trackWidth = field.track_width
                this.field.headlandTracks = field.config.length
                this.field.headlands = field.config.map(val => val === 1.0)
                this.field.innerBorder = field.inner_border
                this.field.fieldColor = field.color
                this.field.fieldArea = field.area
                this.field.fieldBounds = field.bounds
                this.field.headlandResponse = field.headland
                this.oldABLineCoordinates = field.ab_lines.coordinates[0]
                this.setFieldOwner(field.farm_id)

                // Set field source info
                if (field.quality_comment) {
                    const fieldSourceInfoStore = useFieldSourceInfoStore()
                    fieldSourceInfoStore.setFieldSourceInfo(field.quality_comment)
                }

                // Show the field on the map
                mapStore.centerToField(field)
                this.setFieldStyle()
                this.field.id = field.id
                this.outerBorderDrawed = true

                // add obstacles to context menu
                this.attachContextMenuToObstacles()

                await this.generateHeadlands()
                if (this.field.id || fromTrimble) {
                    await this.generateABLines('originalABlines')
                }
                await this.updateGeneratedABlines()
                await this.optimizeLongestEdge()

                await this.handleFieldProblems()

                // set default preview (Color picker)
                this.setDefaultPreview()

                return true
            } catch (error) {
                console.error('There was a problem with the edit operation:', error)
                return false
            }
        },
        setDefaultPreview() {
            const fls = useFieldLayersStore()
            
            // Find layers
            const fieldLayer = fls.layers.find(layer => layer.id === 'field')
            const outerBorderLayer = fls.layers.find(layer => layer.id === 'outer_border')
            const headlandLayer = fls.layers.find(layer => layer.id === 'headland')
            const abLinesLayer = fls.layers.find(layer => layer.id === 'ab_lines')
            const segmentsLayer = fls.layers.find(layer => layer.id === 'segments')
            const trackWheelsLayer = fls.layers.find(layer => layer.id === 'track_wheels')
            
            // Set visible properties
            if (fieldLayer) fieldLayer.visible = false
            if (outerBorderLayer) outerBorderLayer.visible = true
            if (headlandLayer) headlandLayer.visible = true
            if (abLinesLayer) abLinesLayer.visible = false
            if (segmentsLayer) segmentsLayer.visible = false
            if (trackWheelsLayer) trackWheelsLayer.visible = false
            
            // Call toggleLayerVisibility for each
            if (fieldLayer) fls.toggleLayerVisibility(fieldLayer)
            if (outerBorderLayer) fls.toggleLayerVisibility(outerBorderLayer)
            if (headlandLayer) fls.toggleLayerVisibility(headlandLayer)
            if (abLinesLayer) fls.toggleLayerVisibility(abLinesLayer)
            if (segmentsLayer) fls.toggleLayerVisibility(segmentsLayer)
            if (trackWheelsLayer) fls.toggleLayerVisibility(trackWheelsLayer)
            
            // Change colors
            if (headlandLayer) fls.updateLayerColor(headlandLayer, '#40E0D0')
            if (outerBorderLayer) fls.updateLayerColor(outerBorderLayer, '#fb00f6')
        },
        hideABLinesAndTrackWheelsLayers() {
            const fls = useFieldLayersStore()
            const abLinesLayer = fls.getLayer('ab_lines')
            const trackWheelsLayer = fls.getLayer('track_wheels')

            if (abLinesLayer) {
                abLinesLayer.visible = false
                fls.toggleLayerVisibility(abLinesLayer)
            }
            if (trackWheelsLayer) {
                trackWheelsLayer.visible = false
                fls.toggleLayerVisibility(trackWheelsLayer)
            }
        },
        restoreFieldLayers() {
            // restore field layers to default (meaning is redirect to field details page after saving field)
            const fls = useFieldLayersStore()
            
            // Find layers
            const fieldLayer = fls.layers.find(layer => layer.id === 'field')
            const outerBorderLayer = fls.layers.find(layer => layer.id === 'outer_border')
            const headlandLayer = fls.layers.find(layer => layer.id === 'headland')
            const abLinesLayer = fls.layers.find(layer => layer.id === 'ab_lines')
            const segmentsLayer = fls.layers.find(layer => layer.id === 'segments')
            const trackWheelsLayer = fls.layers.find(layer => layer.id === 'track_wheels')
            const roiEntrypointsLayer = fls.layers.find(layer => layer.id === 'roi_entrypoints')
            const roiObstaclesLayer = fls.layers.find(layer => layer.id === 'roi_obstacles')
            const roiLogisticsLayer = fls.layers.find(layer => layer.id === 'roi_logistics')
            
            // Set visible properties
            if (fieldLayer) fieldLayer.visible = true
            if (outerBorderLayer) outerBorderLayer.visible = true
            if (headlandLayer) headlandLayer.visible = true
            if (abLinesLayer) abLinesLayer.visible = true
            if (segmentsLayer) segmentsLayer.visible = true
            if (trackWheelsLayer) trackWheelsLayer.visible = false
            if (roiEntrypointsLayer) roiEntrypointsLayer.visible = true
            if (roiObstaclesLayer) roiObstaclesLayer.visible = true
            if (roiLogisticsLayer) roiLogisticsLayer.visible = true
            
            // Call toggleLayerVisibility for each
            if (fieldLayer) fls.toggleLayerVisibility(fieldLayer)
            if (outerBorderLayer) fls.toggleLayerVisibility(outerBorderLayer)
            if (headlandLayer) fls.toggleLayerVisibility(headlandLayer)
            if (abLinesLayer) fls.toggleLayerVisibility(abLinesLayer)
            if (segmentsLayer) fls.toggleLayerVisibility(segmentsLayer)
            if (trackWheelsLayer) fls.toggleLayerVisibility(trackWheelsLayer)
            if (roiEntrypointsLayer) fls.toggleLayerVisibility(roiEntrypointsLayer)
            if (roiObstaclesLayer) fls.toggleLayerVisibility(roiObstaclesLayer)
            if (roiLogisticsLayer) fls.toggleLayerVisibility(roiLogisticsLayer)
        },
        async optimizeLongestEdge() {
            if (!this.shouldGenerateABLines()) {
                return true
            }

            this.loading.longestEdge = true

            try {
                if (!this.headlandGenerated) {
                    this.handleFieldTooSmallError('headlands')
                    throw new Error('Headlands must be generated first')
                }

                const data = {
                    track_width: this.field.trackWidth.toFixed(1),
                    headland: this.field.headlandResponse,
                    outer_border: {
                        type: 'FeatureCollection',
                        features: [{
                            id: '0',
                            type: 'Feature',
                            properties: { field_geom_type: 'outer_border' },
                            geometry: {
                                type: "Polygon",
                                coordinates: this.field.outerBorder.coordinates
                            }
                        }]
                    }
                }

                this.turnOffEditablePolygon()

                const response = await postGenerateABLinesOptions(data)
                // Keep custom AB lines (e.g. Points on field border) even when defaults are empty.
                const hasAnyABLinesGenerated = this.hasAnyValidABLinesOption(response)
                    || this.hasValidCustomABLinesInField()
                if (!response) {
                    this.handleFieldTooSmallError('no_response_from_ab_options_generation')
                    throw new Error('No response from AB options generation')
                } else if (!hasAnyABLinesGenerated) {
                    this.handleFieldTooSmallError('fieldTooSmallForABLines')
                    throw new Error('No AB lines generated from AB options generation')
                }

                this.field.abLinesOptions = response

                // clear ab lines
                this.clearABLinesMap()

                // add new ab lines
                if (response && response.longest_edge && response.longest_edge.ab_lines) {
                    // Set correct ID for first feature to prevent headland overlap
                    response.longest_edge.ab_lines.features[0].id = this.field.headlandResponse.features.length
                    
                    // add the new lines to map only if we are not in edit mode
                    if (!this.field.id) {
                        // this.showABLines()
                        // LEAVE THIS COMMENTED FOR NOW
                        // if (this.drawedABlineCoordinates === null && this.parallelLineCoordinates.length === 0 && this.selectedFieldFromDB === null && this.uploadedFile === null) {
                        //     mapStore.map.data.addGeoJson(response.longest_edge.ab_lines)
                        // }
                    } else {
                        if (this.field.abLines.originalABlines) {
                            // Add as Polylines directly on map (not in map.data) to avoid TerraDraw conflicts
                            this.addABLinesAsPolylines(this.field.abLines.originalABlines.ab_lines)
                        }
                    }

                    this.field.abLines.longestEdge = response.longest_edge
                }

                // Set correct IDs for all other options
                Object.keys(response).forEach(key => {
                    if (response[key]?.ab_lines?.features?.length > 0) {
                        response[key].ab_lines.features[0].id = this.field.headlandResponse.features.length
                    }
                })

                this.abLinesOptionsUpdate()
                this.ablinesGenerated = this.hasAnyValidABLinesOption(this.field.abLinesOptions)
                if (!this.ablinesGenerated) {
                    this.handleFieldTooSmallError('fieldTooSmallForABLines')
                    throw new Error('No AB lines generated from AB options generation')
                }
                return true
            } catch (error) {
                console.error('AB Lines generation failed:', error)
                return false
            } finally {
                this.loading.longestEdge = false
                this.turnOnEditablePolygon()
            }
        },
        checkFieldValidity() {
            // Check if field outer border intersects with itself
            if (this.doEdgesIntersect(toRaw(this.field.shapes[0]).getPath().getArray())) {
                return 'field_edges_intersect'
            }

            // Check if obstacle outer border intersects with itself
            for (let i = 0; i < this.field.cutouts.length; i++) {
                if (this.doEdgesIntersect(toRaw(this.field.cutouts[i]).getPath().getArray())) {
                    return 'obstacle_edges_intersect'
                }
            }

            // Check if obstacles are inside field
            for (let i = 0; i < this.field.cutouts.length; i++) {
                if (!this.isObstacleInsideField(toRaw(this.field.cutouts[i]))) {
                    return 'obstacle_outside_field'
                }
            }

            if (!this.isObstacleInsideField(toRaw(this.field.shapes[0]))) {
                return 'obstacle_outside_field'
            }

            // Check if obstacles are inside another obstacle
            for (let i = 0; i < this.field.cutouts.length; i++) {
                const currentObstacle = toRaw(this.field.cutouts[i])
                // Exclude the current obstacle from the check
                const otherObstacles = this.field.cutouts.filter(index => index !== i)
                
                for (const otherObstacle of otherObstacles) {
                    if (this.isObstacleInsideAnotherObstacle(currentObstacle, toRaw(otherObstacle))) {
                        return 'obstacle_inside_obstacle'
                    }
                }
            }
            
            return null
        },
        async saveFieldToDB(navigate = true) {
            const mapStore = useMaps()
            const customersStore = useCustomers()

            this.loading.saveField = true

            try {
                if (this.field.selectedABOption === null) {
                    return {
                        ok: false,
                        reason: 'AB lines option is not selected',
                        info: ''
                    }
                }

                if (customersStore.customers.length === 1) {
                    this.selectedCustomer = customersStore.customers[0]
                } else if (!this.selectedCustomer) {
                    return {
                        ok: false,
                        reason: 'Customer is not selected',
                        info: ''
                    }
                }

                // 1. rewind outer border
                const outerBorderCoords = this.rewindRing(
                    this.field.outerBorder.coordinates[0],
                    true
                )

                // 2. rewind obstacles
                const obstacleCoords = this.field.cutouts.map(cutout => {
                    const coords = cutout.getPath().getArray().map(coord => [coord.lng(), coord.lat()])
                    return this.rewindRing(coords, false)
                })
                const data = this.prepareDataForSave(outerBorderCoords, obstacleCoords)
                const formData = new FormData()
                formData.append('data', JSON.stringify(data))
                if (this.surveyOuterBorderFile !== null) {
                    formData.append('outer_border', this.surveyOuterBorderFile)
                }

                // append ab lines if ab lines direction was uploaded
                if (this.field.selectedABOption === 'uploaded_file' && this.surveyABLineFile !== null) {
                    formData.append('ab_line', this.surveyABLineFile)
                }

                // append curved ab lines if curved ab lines direction was uploaded
                if (this.field.selectedABOption === 'curved_ab_lines' && this.surveyCurvedABlines !== null) {
                    formData.append('ab_line', this.surveyCurvedABlines)
                }

                // append cutouts if cutouts were uploaded
                if (this.surveyCutoutsFiles.length > 0 && this.surveyCutoutsIDs.length > 0) {
                    for (let i = 0; i < this.surveyCutoutsFiles.length; i++) {
                        formData.append(`cutout_${i}`, this.surveyCutoutsFiles[i])
                    }
                }

                if (this.field.id) {
                    data.id = this.field.id
                    const taskStore = useTasksStore()
                    taskStore.reset()
                }

                const response = this.field.id ? await updateField(data) : await postSaveFieldWithSurvey(formData)

                const isHttpError =
                    response?.ok === false ||
                    (typeof response?.status === 'number' && response.status >= 400)

                if (isHttpError) {
                    return {
                        ok: false,
                        reason: response?.data?.reason || response?.problem || 'Request failed',
                        info: response?.data?.info || ''
                    }
                }

                const fieldId = response?.id
                if (fieldId == null || fieldId === '') {
                    return {
                        ok: false,
                        reason: 'Missing field id in response',
                        info: ''
                    }
                }

                this.fieldSaved = true
                if (!navigate) {
                    return { ok: true, reason: '', info: '', id: fieldId }
                }

                await this.clearDrawing(this.hasPaths)
                await mapStore.getAllFields(false)
                await mapStore.navToField(fieldId, '/details')
                this.field.name = ''
                this.restoreFieldLayers()
                return { ok: true, reason: '', info: '', id: fieldId }
            } catch (error) {
                console.error('There was a problem with the fetch operation:', error)
                return {
                    ok: false,
                    reason: error?.message || 'Unexpected error',
                    info: ''
                }
            } finally {
                this.loading.saveField = false
            }
        },
        prepareDataForSave(outerBorderCoords, obstacleCoords) {
            const outerBorderData = {
                type: 'FeatureCollection',
                features: [
                    {
                        id: '0',
                        type: 'Feature',
                        properties: {
                            field_geom_type: 'outer_border'
                        },
                        geometry: {
                            type: "Polygon",
                            coordinates: [
                                outerBorderCoords,
                                ...obstacleCoords
                            ]
                        }
                    }
                ]
            }

            const seg = useSegmentsStore()
            let headlandSegmentsData = null
            if (seg.allSegmentsLinesCoords.length > 0) {
                // Each segment is a GeometryCollection that contains a MultiLineString
                headlandSegmentsData = {
                    type: 'FeatureCollection',
                    features: seg.allSegmentsLinesCoords.map((geomCollection, index) => {
                        return {
                            type: 'Feature',
                            properties: {
                                field_geom_type: 'headland_segments',
                                counter: index
                            },
                            geometry: geomCollection
                        }
                    })
                }
            }

            // Main data for saving
            const data = {
                farm_id: this.selectedCustomer.id,
                track_width: this.field.trackWidth,
                config: this.field.headlands.slice(0, this.field.headlandTracks).map(value => value ? 1.0 : 0.5),
                field_name: this.field.name.trim(),
                generator_version: this.field.headlandResponse.generator_version,
                ab_lines_orientation_degrees: this.field.abLinesOptions[this.field.selectedABOption].ab_orientation_angle
            }

            const isAdmin = this.hasAdminAccess()

            if (isAdmin) {
                // Admin users can select specific changes to be saved
                if (this.selectedChangesOptions.includes('ab_lines')) {
                    data.ab_lines = this.field.abLinesOptions[this.field.selectedABOption].ab_lines
                } else {
                    // if ab lines are not selected, don't send ab_lines_orientation_degrees
                    delete data.ab_lines_orientation_degrees
                }
                if (this.selectedChangesOptions.includes('headland')) {
                    data.headland = this.field.headlandResponse
                    if (headlandSegmentsData) {
                        data.headland_segments = headlandSegmentsData
                    }
                }
                if (this.selectedChangesOptions.includes('outer_border')) {
                    data.outer_border = outerBorderData
                }
            } else {
                // Non-admin users or admin users without selection: send everything automatically
                data.ab_lines = this.field.abLinesOptions[this.field.selectedABOption].ab_lines
                data.headland = this.field.headlandResponse
                if (headlandSegmentsData) {
                    data.headland_segments = headlandSegmentsData
                }
                data.outer_border = outerBorderData
            }

            // append commit message if it exists and field is being updated
            const textAreaStore = useTextAreaStore()
            if (this.field.id && textAreaStore.message) {
                data.commit_msg = textAreaStore.message
            }

            // append quality comment if it exists and field is being updated
            const fieldSourceInfoStore = useFieldSourceInfoStore()
            if (fieldSourceInfoStore.getSourceInfoType().hasLabel || fieldSourceInfoStore.isOldSourceInfo) {
                data.quality_comment = fieldSourceInfoStore.getSourceInfoType().value
            }

            return data
        },
        async uploadFieldFile(file = null, makeRequest = true, field = null) {
            const formData = new FormData()
            const mapStore = useMaps()
            const umbs = useUploadMultipleBordersStore()

            this.loading.fileUpload = true
            
            if (Array.isArray(file)) {
                // Multiple files case
                file.forEach((f, index) => {
                    formData.append(`border_${index}`, f)
                })
            } else if (file) {
                // Single file case
                formData.append('outer_border', file)
            }
        
            /**
             * Instead of clearing everything, just clear the shapes and cutouts
             * Use this for replace field border
             */
            this.field.shapes.forEach(shape => toRaw(shape).setMap(null));
            this.field.cutouts.forEach(cutout => toRaw(cutout).setMap(null));
            
            this.field.shapes = [];
            this.field.outerBorder = null;
            this.field.cutouts = [];
            this.isModified = false
            this.field.requestHeadlandData = null
            this.field.selectedABOption = null

            // Reset field source info
            const fieldSourceInfoStore = useFieldSourceInfoStore()
            fieldSourceInfoStore.resetFieldSourceInfo()
        
            try {
                const response = makeRequest ? await postTrackSystemBorderArrayFromUpload(formData) : field

                // Handle field null
                if (response === null) {
                    return 'field_null'
                }

                // Check if the response is an array or a single object
                const responseArray = Array.isArray(response) ? response : [response];

                if (responseArray.length > 1) {
                    // if we have multiple files, we need to call the function that will handle the multiple files
                    umbs.availableFields = responseArray
                    umbs.sourceFileName = file?.name || 'File name not found'
                    umbs.hasMultipleFiles = true
                    return true
                }

                // For a single file, use the first element from the array
                const singleResponse = responseArray[0]
                
                if (!singleResponse || !singleResponse.outer_border || !singleResponse.outer_border.features) {
                    throw new Error('Invalid response format!')
                }

                // Set field source info
                fieldSourceInfoStore.setFieldSourceInfo(singleResponse.outer_border.features[0].properties)

                const fieldProblemsStore = useFieldProblemsStore()
                fieldProblemsStore.checkFieldProblems(singleResponse)
                
                const svgStore = useSvgStore()
                svgStore.setEditMode(false)

                this.isStartingDrawing = true;
                this.outerBorderDrawed = true;
                if (this.field.id) {
                    mapStore.hideAllBorders(this.field.id)
                } else {
                    mapStore.hideAllBorders()
                }
                this.fieldsHidden = true
        
                // Initialize cutouts array
                this.field.cutouts = [];
                const bulkInProgress = umbs.bulkInProgress
        
                // Process the response and add shapes and cutouts
                singleResponse.outer_border.features.forEach(feature => {
                    const coordinates = feature.geometry.coordinates;
        
                    // Add outer border
                    if (feature.properties.field_geom_type === 'outer_border') {
                        const outerBorderPath = geoJsonRingToOpenPath(coordinates[0])
        
                        const outerBorderPolygon = new mapStore.google.maps.Polygon({
                            paths: outerBorderPath,
                            editable: !bulkInProgress,
                            strokeColor: 'red',
                            strokeWeight: 2,
                            fillColor: 'transparent',
                            fillOpacity: 0,
                            zIndex: 1
                        });
        
                        outerBorderPolygon.setMap(mapStore.map);
                        toRaw(this.field.shapes).push(outerBorderPolygon);
                        this.addHandleUpdateListeners(outerBorderPolygon)
        
                        // Set outerBorder and pass coordinates through rewindRing
                        const outerBorderCoordinates = this.rewindRing(coordinates[0], true);
                        this.field.outerBorder = {
                            type: 'Polygon',
                            coordinates: [outerBorderCoordinates]
                        };
        
                        // Calculate bounds using outerBorder and center the map
                        const bounds = new mapStore.google.maps.LatLngBounds();
                        coordinates[0].forEach(coord => {
                            bounds.extend(new mapStore.google.maps.LatLng(coord[1], coord[0]));
                        });
        
                        mapStore.centerToField({ bounds: bounds });
        
                        // Add obstacles (cutouts)
                        for (let i = 1; i < coordinates.length; i++) {
                            const cutoutPath = geoJsonRingToOpenPath(coordinates[i])
        
                            const cutoutPolygon = new mapStore.google.maps.Polygon({
                                id: uuid(),
                                paths: cutoutPath,
                                editable: !bulkInProgress,
                                strokeColor: 'red',
                                strokeWeight: 2,
                                fillColor: 'transparent',
                                fillOpacity: 0,
                                zIndex: 1
                            });
        
                            cutoutPolygon.setMap(mapStore.map);
                            this.field.cutouts.push(cutoutPolygon);
                            this.addHandleUpdateListeners(cutoutPolygon)
                        }
                    }
                });
        
                this.setFieldStyle();
                // add field to context menu
                this.handleContextMenu(this.field.shapes[0])
                // add obstacles to context menu
                this.attachContextMenuToObstacles()
                if (!bulkInProgress && mapStore.terraDraw) {
                    // Ensure TerraDraw is started before setting mode
                    if (!mapStore.terraDraw.enabled) {
                        mapStore.terraDraw.start()
                    }
                    mapStore.terraDraw.setMode('select')
                }

                this.turnOffEditablePolygon()
                await this.generateHeadlands()
                if (this.field.id) {
                    await this.generateABLines('originalABlines')
                }
                await this.updateGeneratedABlines()
                await this.optimizeLongestEdge()
                this.turnOnEditablePolygon()

                // Single upload: name from file. Multi-field select: file is null, use geo properties.
                if (file?.name) {
                    this.field.name = removeFilenameExtension(file.name)
                } else {
                    const name = getFieldDisplayName(singleResponse, umbs.currentFieldIndex)
                    if (name) this.field.name = name
                }

                const success = this.checkFieldValidity()
                if (success !== null) {
                    this.fieldValidationError = true
                    return success
                } else {
                    this.fieldValidationError = false
                }

                return true;
            } catch (error) {
                console.error('There was a problem with the fetch operation:', error);
                await this.clearDrawing()
                return false;
            } finally {
                this.loading.fileUpload = false
            }
        },
        async autoCorrectBulkBorderProblems() {
            const fieldProblemsStore = useFieldProblemsStore()
            const umbs = useUploadMultipleBordersStore()
            const fieldName = umbs.bulkUploadState.name

            const headlandOk = await this.generateHeadlands()
            if (!headlandOk) return 'headland_failed'

            // Fixing one problem can reveal others, so allow more rounds than initial problems
            const maxAttempts = Math.max(50, fieldProblemsStore.markers.length * 2)
            // The same border twice in a row means the last suggestion changed nothing
            const maxSameBorder = 2
            let attempts = 0
            let consecutiveSame = 0
            let lastBorderJson = null

            if (fieldProblemsStore.markers.length > 0) {
                umbs.setBulkUploadState(fieldName, 'Correcting border')
            }

            while (fieldProblemsStore.markers.length > 0 && attempts < maxAttempts) {
                if (!this.field.requestHeadlandData?.outer_border) return false

                const res = await fieldProblemsStore.correctBorder(0)
                if (!res?.outer_border) return false

                const borderJson = JSON.stringify(res.outer_border)
                consecutiveSame = borderJson === lastBorderJson ? consecutiveSame + 1 : 1
                lastBorderJson = borderJson

                if (consecutiveSame >= maxSameBorder) return 'max_attempts'

                fieldProblemsStore.applySuggestion()
                this.prepareHeadlandRequestData()
                await fieldProblemsStore.checkBorder()
                attempts++
            }

            if (fieldProblemsStore.markers.length > 0) return 'max_attempts'

            if (attempts > 0) {
                umbs.setBulkUploadState(fieldName, 'Generating headland')
                const regenerated = await this.generateHeadlands()
                if (!regenerated) return 'headland_failed'
            }

            return true
        },
        selectFewestABLinesOption() {
            const options = this.field.abLinesOptions
            if (!options) {
                this.field.selectedABOption = null
                return
            }

            let bestKey = null
            let bestCount = Infinity
            Object.entries(options).forEach(([key, option]) => {
                const count = option?.lines_count || 0
                if (count > 0 && count < bestCount) {
                    bestCount = count
                    bestKey = key
                }
            })
            this.field.selectedABOption = bestKey
        },
        isPolygonInsidePolygon(innerPolygon, outerPolygon) {
            const mapStore = useMaps()
            const points = innerPolygon.getPath().getArray()

            for (let i = 0; i < points.length; i++) {
                if (!mapStore.google.maps.geometry.poly.containsLocation(points[i], outerPolygon)) {
                    return false
                }
            }
            return true
        },
        rewindRing(ring, clockwise) {
            let area = 0;

            for (let i = 0, len = ring.length, j = len - 1; i < len; j = i++) {
                const p1 = ring[i];
                const p2 = ring[j];

                // Check if p1 and p2 are Google Maps LatLng objects or coordinate arrays
                if (typeof p1.lng === 'function' && typeof p2.lng === 'function') {
                    // Google Maps LatLng objects
                    area += (p1.lng() * p2.lat())
                    area -= (p1.lat() * p2.lng())
                } else if (Array.isArray(p1) && Array.isArray(p2)) {
                    // Coordinate arrays [lng, lat]
                    area += (p1[0] * p2[1])
                    area -= (p1[1] * p2[0])
                } else {
                    // Plain {lat, lng} objects
                    area += (p1.lng * p2.lat)
                    area -= (p1.lat * p2.lng)
                }
            }

            const currentDirection = area >= 0
            if (currentDirection !== clockwise) {
                ring.reverse()
            }

            return ring
        },
        async drawObstacle(id) {
            const mapStore = useMaps()
            let cutout = null
            let coordinates = []
            
            if (this.isStartingDrawing) {
                cutout = this.field.cutouts.find(cutout => cutout.id === id)
                if (!cutout) {
                    return
                }
                coordinates = cutout.getPath().getArray().map(coord => [coord.lng(), coord.lat()])
            } else {
                const roi = useRoiStore()
                cutout = roi.geometryElements.find(cutout => cutout.id === id)
                if (!cutout) {
                    return
                }
                coordinates = cutout.path.map(coord => [coord.lng, coord.lat])
            }

            // Get geometry type (polygon is default if not specified)
            const isPolygon = cutout.type === 'polygon' || !cutout.type
            const isLine = cutout.type === 'line'
            const geometryType = isPolygon ? 'Polygon' : (isLine ? 'LineString' : 'LineString')

            // Close polygon if it is not closed
            if (isPolygon && coordinates.length > 0 && 
                (coordinates[0][0] !== coordinates[coordinates.length - 1][0] || 
                 coordinates[0][1] !== coordinates[coordinates.length - 1][1])) {
                coordinates.push(coordinates[0])
            }

            let obstacle = {
                id: id,
                type: geometryType,
                outer_border: {
                    type: geometryType,
                    coordinates: isPolygon ? [coordinates] : coordinates
                },
            }

            await mapStore.drawGeoSVG(obstacle)
            this.drawObstacleLabel(id)
        },
        drawObstacleLabel(id) {
            const mapStore = useMaps()

            let cutout = this.field.cutouts.find(cutout => cutout.id === id)
            if (!cutout) return

            let bounds = new mapStore.google.maps.LatLngBounds()
            cutout.getPath().getArray().forEach(coord => bounds.extend(coord))

            let center = bounds.getCenter()

            let content = document.createElement('span')

            let index = this.field.cutouts.findIndex(cutout => cutout.id === id)
            content.innerHTML = index + 1
            content.classList.add('popup-bubble')

            let containerDiv = document.createElement('div')
            containerDiv.classList.add('popup-container')
            containerDiv.appendChild(content)

            if (this.field.markers[id]) {
                toRaw(this.field.markers[id]).setMap(null)
            }

            this.field.markers[id] = new mapStore.google.maps.marker.AdvancedMarkerElement({
                map: toRaw(mapStore.map),
                position: center,
                content: containerDiv,
                gmpClickable: true
            })

            this.field.shapes[0].addListener('click', () => {
                this.handleContextMenu(this.field.shapes[0])
            })

            this.field.markers[id].addEventListener('gmp-click', () => {
                this.centerToObstacle(cutout)
            })
        },
        getObstacleArea(id) {
            let cutout = null
            let coordinates = []

            if (this.isStartingDrawing) {
                cutout = this.field.cutouts.find(cutout => cutout.id === id)
                if (!cutout) {
                    return 0
                }
                coordinates = cutout.getPath().getArray().map(coord => [coord.lng(), coord.lat()])
            } else {
                const roi = useRoiStore()
                cutout = roi.geometryElements.find(cutout => cutout.id === id)
                if (!cutout) {
                    return 0
                }
                coordinates = cutout.path.map(coord => [coord.lng, coord.lat])
            }

            // Get geometry type (polygon is default if not specified)
            const isPolygon = cutout.type === 'polygon' || !cutout.type

            // Close the polygon if it is not closed
            if (isPolygon && coordinates.length > 0 && 
                (coordinates[0][0] !== coordinates[coordinates.length - 1][0] || 
                 coordinates[0][1] !== coordinates[coordinates.length - 1][1])) {
                coordinates.push(coordinates[0])
            }

            // Need area only for Polygon
            if (!isPolygon) {
                return 0
            }

            let geojson = {
                type: 'FeatureCollection',
                features: [{
                    type: 'Feature',
                    geometry: {
                        type: 'Polygon',
                        coordinates: [coordinates]
                    }
                }]
            }

            let area = turf.area(geojson)
            return area
        },
        getObstacleLength(id) {
            const roi = useRoiStore()
            const cutout = roi.geometryElements.find(cutout => cutout.id === id)
            if (!cutout) {
                return 0
            }
            let coordinates = cutout.path.map(coord => [coord.lng, coord.lat])
            let geojson = {
                type: 'FeatureCollection',
                features: [{
                    type: 'Feature',
                    geometry: {
                        type: 'LineString',
                        coordinates: coordinates
                    }
                }]
            }
            let length = turf.length(geojson)
            return length
        },
        centerToObstacle(cutout) {
            const mapStore = useMaps()
            const bounds = new mapStore.google.maps.LatLngBounds()
            cutout.getPath().getArray().forEach(coord => bounds.extend(coord))
            mapStore.map.fitBounds(bounds)

            if (cutout.getPath().getLength() > 3) {
                this.handleContextMenu(cutout)
            } else {
                this.deleteMenu.close()
                this.deleteMenu.removeListener()
            }
        },
        async updateFieldName(field, name) {
            const mapStore = useMaps()

            const data = { 
                id: field.id,
                field_name: name
            }

            try {
                const response = await updateField(data)
                if (!response) {
                    throw new Error('Failed to update field name!')
                }

                await mapStore.getField(field.id)

                return true
            } catch (error) {
                console.error('There was a problem with the fetch operation:', error)
                return false
            }

        },
        resetField() {
            this.field = this.$options.state().field
        },
        resetState() {
            Object.assign(this.$state, this.$options.state())
        },
        handleOuterBorderPolygon(event) {
            toRaw(this.field.shapes).push(event.overlay)
            event.overlay.setOptions({
                fillColor: "#FF0000",
                fillOpacity: 0.1,
            })
            this.outerBorderDrawed = true
            this.isStartingDrawing = true

            // Add vertex/path change listeners
            this.addHandleUpdateListeners(event.overlay)
        },
        async handleObstacleDrawing(obstacle) {

            const eventObject = {
                overlay: obstacle,
                type: 'polygon'
            }

            const mapStore = useMaps()
            try {
                // check if obstacle is inside field
                if (!this.isObstacleInsideField(obstacle)) {
                    this.obstacleErrorType = 'outside_field'
                    await this.handleInvalidPolygon(eventObject)
                    return false
                }

                // check if obstacle is inside another obstacle
                if (this.isObstacleInsideAnotherObstacle(obstacle)) {
                    this.obstacleErrorType = 'obstacle_overlap'
                    await this.handleInvalidPolygon(eventObject)
                    return false
                }

                // get obstacle path and reorient it
                let obstaclePath = obstacle.getPath().getArray()
                let obstacleCoords = obstaclePath.map(point => [point.lng(), point.lat()])
                
                // close polygon if it is not closed
                if (obstacleCoords[0][0] !== obstacleCoords[obstacleCoords.length - 1][0] || 
                    obstacleCoords[0][1] !== obstacleCoords[obstacleCoords.length - 1][1]) {
                    obstacleCoords.push(obstacleCoords[0])
                }

                for (let i = 0; i < this.field.shapes.length; i++) {
                    if (this.isPolygonInsidePolygon(obstacle, this.field.shapes[i])) {
                        // Reorient obstacle counterclockwise for inner rings
                        let innerPath = stripClosingDuplicatePoint(this.rewindRing(obstacleCoords, false))
                        
                        // Assign a unique ID to the obstacle early
                        obstacle.id = uuid()
                        
                        // First add to cutouts list so we can track it
                        this.field.cutouts.push(obstacle)
                        
                        // Modify the original obstacle to match our required styles
                        obstacle.setOptions({
                            strokeColor: 'red',
                            strokeWeight: 2,
                            fillColor: 'transparent',
                            fillOpacity: 0,
                            zIndex: 999,
                            editable: true,
                            // draggable: true
                        })
                        
                        // Create a path from inner ring coordinates
                        const newPath = new mapStore.google.maps.MVCArray(
                            innerPath.map(coord => new mapStore.google.maps.LatLng(coord[1], coord[0]))
                        )
                        
                        obstacle.setPath(newPath)
                        
                        // Add the path to the paths collection of the main polygon
                        const pathsCollection = this.field.shapes[i].getPaths()
                        pathsCollection.push(newPath)
                        
                        // Add context menu to obstacle
                        this.handleContextMenu(obstacle)
                        this.addHandleUpdateListeners(obstacle)
                        
                        // Add label to obstacle
                        this.drawObstacleLabel(obstacle.id)
                        
                        // Stop drawing mode
                        this.isDrawingObstacle = false
                        if (mapStore.terraDraw && mapStore.terraDraw.enabled) {
                            mapStore.terraDraw.setMode('select')
                            mapStore.terraDraw.stop()
                        }
                        
                        // Regenerate headlands and AB lines
                        this.turnOffEditablePolygon()
                        await this.generateHeadlands()
                        if (this.field.id) {
                            await this.generateABLines('originalABlines')
                        }
                        await this.updateGeneratedABlines()
                        await this.optimizeLongestEdge()
                        this.turnOnEditablePolygon()

                        // Draw SVG after everything is ready
                        setTimeout(async () => {
                            await this.drawObstacle(obstacle.id)
                        }, 500)
                        
                        this.showFieldInterior = false
                        this.setFieldStyle()

                        return true
                    }
                }
            } catch (error) {
                console.error('Error handling obstacle:', error)
                return false
            }
        },
        async removeObstacle(id) {
            const mapStore = useMaps()
        
            try {
                const index = this.field.cutouts.findIndex(cutout => cutout.id === id)
                if (index === -1) {
                    return false
                }
        
                const obstacle = toRaw(this.field.cutouts[index])

                // 1. remove all event listeners
                if (obstacle) {
                    mapStore.google.maps.event.clearInstanceListeners(obstacle)
                    if (obstacle.getPath) {
                        mapStore.google.maps.event.clearInstanceListeners(obstacle.getPath())
                    }
                }
        
                // 2. remove obstacle from map
                if (obstacle) {
                    // useful for uploaded fields with custom event listeners
                    // obstacle.unbindAll is not a function in google maps
                    if (typeof obstacle.unbindAll === 'function') {
                        obstacle.unbindAll()
                    }
                    if (typeof obstacle.setMap === 'function') {
                        obstacle.setMap(null)
                    }
                }
                
                // 3. remove marker
                if (this.field.markers[id]) {
                    const marker = toRaw(this.field.markers[id])
                    if (marker && typeof marker.setMap === 'function') {
                        marker.setMap(null)
                    }
                    delete this.field.markers[id]
                }
        
                // 4. remove from cutouts array
                this.field.cutouts.splice(index, 1)
                
                // 5. remove from survey cutouts IDs
                // We cannot use index because it is not the same index as the cutouts array
                this.surveyCutoutsIDs = this.surveyCutoutsIDs.filter(cutoutId => cutoutId !== id)
        
                // 6. update field shapes
                const fieldShape = toRaw(this.field.shapes[0])
                if (fieldShape && fieldShape.getPaths) {
                    const paths = fieldShape.getPaths()
                    if (paths) {
                        const newPaths = new mapStore.google.maps.MVCArray()
                        
                        // add outer border
                        newPaths.push(paths.getAt(0))
                        
                        // add remaining cutouts
                        this.field.cutouts.forEach(cutout => {
                            const cutoutPath = cutout.getPath()
                            if (cutoutPath) {
                                newPaths.push(cutoutPath)
                            }
                        })
                        
                        fieldShape.setPaths(newPaths)
                    }
                }
        
                // 7. regenerate headlands and ab lines
                this.turnOffEditablePolygon()
                await this.generateHeadlands()
                if (this.field.id) {
                    await this.generateABLines('originalABlines')
                }
                await this.optimizeLongestEdge()
                await this.updateGeneratedABlines()
                this.turnOnEditablePolygon()

                // 8. Check all remaining obstacles for validity
                await this.handleFieldProblems()

                // 9. redraw the remain obstacles labels
                this.field.cutouts.forEach(cutout => {
                    this.drawObstacleLabel(cutout.id)
                })
        
                return true
            } catch (error) {
                console.error('Error removing obstacle:', error)
                return false
            }
        },
        // add context menu to all obstacles
        attachContextMenuToObstacles() {
            this.field.cutouts.forEach(obstacle => {
                if (obstacle) {
                    this.handleContextMenu(obstacle)
                }
            })
        },
        async toggleSelectBorder() {
            this.showSelectBorder = !this.showSelectBorder

            if (this.field.id === null && this.steps != 4) {
                this.field.selectedABOption = null
            }

            if (this.showSelectBorder) {
                if (this.showDrawDirectionLine) {
                    this.clearTempDrawing()
                }
                if (this.showSelectBorderPoints) {
                    await this.toggleSelectBorderPoints()
                }
                this.clearABLinesMap()
                this.selectBorderLine()
                this.turnOffEditablePolygon()
                this.applyParallelLineSelectionStyle()
            } else {
                this.removeBorderListener()
                if (!this.showDrawDirectionLine && !this.showSelectBorderPoints) {
                    this.turnOnEditablePolygon()
                }
                this.setFieldStyle()
            }
            return this.showSelectBorder
        },
        removeBorderListener() {
            if (this.field.segments) {
                this.field.segments.forEach(segment => {
                    toRaw(segment).setMap(null)
                })
                this.field.segments = []
            }
        },
        selectBorderLine() {
            const mapStore = useMaps()
            this.field.segments = []

            /**
             * Handle headland segments - get the last headland feature
             */
            if (this.field.headlandResponse && this.field.headlandResponse.features) {
                // Filter to get only headland features
                const headlandFeatures = this.field.headlandResponse.features.filter(
                    feature => feature.properties && feature.properties.field_geom_type === "headland"
                )
                
                // Get the last headland feature (highest counter value)
                if (headlandFeatures.length > 0) {
                    const lastHeadland = headlandFeatures.reduce((prev, current) => {
                        return (prev.properties.counter > current.properties.counter) ? prev : current
                    })
                    
                    // Create segments from the coordinates
                    if (lastHeadland.geometry && lastHeadland.geometry.coordinates && lastHeadland.geometry.coordinates[0]) {
                        const headlandCoords = lastHeadland.geometry.coordinates[0].map(
                            coord => ({ lat: coord[1], lng: coord[0] })
                        )
                        // turquoise color hex
                        this.createSegments(headlandCoords, '#40E0D0', mapStore)
                    }
                }
            }

            /**
             * Handle cutouts headland segments - get the last headland feature for each cutout
             */
            if (this.field.cutouts.length > 0 && this.field.headlandResponse && this.field.headlandResponse.features) {
                // Filter to get only headland features
                const headlandFeatures = this.field.headlandResponse.features.filter(
                    feature => feature.properties && feature.properties.field_geom_type === "headland"
                )
                
                if (headlandFeatures.length > 0) {
                    // Get the last headland feature (highest counter value)
                    const lastHeadlandFeature = headlandFeatures.reduce((prev, current) => {
                        return (prev.properties.counter > current.properties.counter) ? prev : current
                    })
                    
                    // For each cutout, find its coordinates in the headland feature
                    if (lastHeadlandFeature.geometry && lastHeadlandFeature.geometry.coordinates) {
                        // Skip the first array (field outer border) and process remaining arrays (cutouts)
                        for (let i = 1; i < lastHeadlandFeature.geometry.coordinates.length; i++) {
                            const cutoutHeadlandCoords = lastHeadlandFeature.geometry.coordinates[i].map(
                                coord => ({ lat: coord[1], lng: coord[0] })
                            )
                            this.createSegments(cutoutHeadlandCoords, '#40E0D0', mapStore)
                        }
                    }
                }
            }
        },
        createSegments(coords, strokeColor, mapStore) {
            for (let i = 0; i < coords.length - 1; i++) {
                this.createSegment([coords[i], coords[i + 1]], strokeColor, mapStore)
            }
            // Add the closing segment
            this.createSegment([coords[coords.length - 1], coords[0]], strokeColor, mapStore)
        },
        transformToClickableField() {
            this.field.shapes.forEach(shape => {
                shape.setOptions({
                    clickable: true
                })
            })
            this.field.cutouts.forEach(cutout => {
                cutout.setOptions({
                    clickable: true
                })
            })
        },
        createSegment(path, strokeColor, mapStore) {
            // Create a new polyline for the segment
            const line = new mapStore.google.maps.Polyline({
                path: path,
                strokeColor: strokeColor,
                strokeOpacity: 1,
                strokeWeight: 5,
                map: toRaw(mapStore.map),
                zIndex: 1000,
                clickable: true
            })

            // Increase stroke weight on mouseover
            line.addListener('mouseover', () => {
                line.setOptions({
                    strokeColor: "turquoise",
                    strokeWeight: 10
                })
            })

            // Restore original stroke weight on mouseout
            line.addListener('mouseout', () => {
                line.setOptions({
                    strokeColor: strokeColor,
                    strokeWeight: 5
                })
            })

            // Handle click event
            line.addListener('click', async () => {
                const coords = line.getPath().getArray()
                this.parallelLineCoordinates = coords
                await this.generateABLines('drawParallelLine')
            })

            toRaw(this.field.segments).push(line)
        },
        turnOffEditablePolygon() {
            const mapStore = useMaps()

            this.field.shapes.forEach(shape => {
                shape.setEditable(false)
            })
            this.field.cutouts.forEach(cutout => {
                cutout.setEditable(false)
            })
            if (mapStore.terraDraw?.enabled) {
                mapStore.terraDraw.setMode('select')
                mapStore.terraDraw.stop()
            }
        },
        turnOnEditablePolygon() {
            const umbs = useUploadMultipleBordersStore()

            if (umbs.bulkInProgress) return

            const mapStore = useMaps()

            this.field.shapes.forEach(shape => {
                shape.setEditable(true)
            })
            this.field.cutouts.forEach(cutout => {
                cutout.setEditable(true)
            })
            if (mapStore.terraDraw) {
                // Ensure TerraDraw is started before setting mode
                if (!mapStore.terraDraw.enabled) {
                    mapStore.terraDraw.start()
                }
                mapStore.terraDraw.setMode('select')
            }
        },
        async updateGeneratedABlines() {
            if (!this.shouldGenerateABLines()) {
                return
            }
            // When field size changed we need to regenerate the AB lines for the new field size
            if (this.drawedABlineCoordinates.length > 0) {
                await this.generateABLines('drawDirectionLine')
            }
        
            if (this.parallelLineCoordinates.length > 0) {
                await this.generateABLines('drawParallelLine')
            }
        
            if (this.selectedFieldFromDB) {
                await this.useDirectionFromExistingField(false)
            }
        
            if (this.uploadedFile) {
                if (this.surveyCurvedABlines !== null) {
                    await this.uploadFile(false, true)
                }
                if (this.surveyABLineFile !== null) {
                    await this.uploadFile(false, false)
                }
            }

            if (this.field.abLines.angle) {
                await this.generateABLines('angle')
            }

            if (this.field.abLines.fromCoordinates) {
                await this.generateABLines('fromCoordinates')
            }

            if (this.field.abLines.fromBorderPoints) {
                await this.generateABLines('fromBorderPoints')
            }
        },
        getLoading() {
            const fieldProblemsStore = useFieldProblemsStore()
            
            return this.loading.abLines || this.loading.dbFields || this.loading.fileUpload || 
                    this.loading.headland || this.loading.longestEdge || fieldProblemsStore.loading
        },
        showABLines() {
            if (this.steps === 4) {
                const selectedOption = this.field.selectedABOption
                if (selectedOption && this.field.abLinesOptions[selectedOption]?.ab_lines?.features?.length > 0) {
                    // Add as Polylines directly on map (not in map.data) to avoid TerraDraw conflicts
                    this.addABLinesAsPolylines(this.field.abLinesOptions[selectedOption].ab_lines)
                }
            }
        },
        disablePolygon() {
            this.field.shapes.forEach((shape) => {
                shape.setOptions({
                    editable: false,
                    clickable: false
                })
            })
            this.field.cutouts.forEach((cutout) => {
                cutout.setOptions({
                    editable: false,
                    clickable: false
                })
            })
        },
        enablePolygon() {
            const umbs = useUploadMultipleBordersStore()

            if (umbs.bulkInProgress) return

            this.field.shapes.forEach((shape) => {
                shape.setOptions({
                    editable: true,
                    clickable: true
                })
            })
            this.field.cutouts.forEach((cutout) => {
                cutout.setOptions({
                    editable: true,
                    clickable: true
                })
            })
        },
        addHandleUpdateListeners(item) {
            const mapStore = useMaps()

            mapStore.google.maps.event.addListener(item.getPath(), 'dragend', () => this.isModified = true)
            mapStore.google.maps.event.addListener(item.getPath(), 'set_at', () => this.isModified = true)
            mapStore.google.maps.event.addListener(item.getPath(), 'insert_at', () => this.isModified = true)
            mapStore.google.maps.event.addListener(item.getPath(), 'remove_at', () => this.isModified = true)
        },
        removeHandleUpdateListeners(item) {
            const mapStore = useMaps()
            
            mapStore.google.maps.event.clearListeners(item.getPath(), 'dragend')
            mapStore.google.maps.event.clearListeners(item.getPath(), 'set_at')
            mapStore.google.maps.event.clearListeners(item.getPath(), 'insert_at')
            mapStore.google.maps.event.clearListeners(item.getPath(), 'remove_at')
        },
        // Define the update handler
        async handleUpdatePolygon() {
            this.disablePolygon()
            const resGenH = await this.generateHeadlands()

            const svgStore = useSvgStore()
            if (svgStore.editMode) {
                svgStore.editMode = false
            }

            await this.handleFieldProblems()

            if (resGenH) {
                if (this.field.id) {
                    await this.generateABLines('originalABlines')
                }
                await this.updateGeneratedABlines()
                await this.optimizeLongestEdge()
            }
            this.enablePolygon()

            this.field.fieldArea = this.calculateFieldArea()
            this.isModified = false
            
            return true
        },
        calculateFieldArea() {
            const geojson = transformBordersToGeoJSON(this.field)
            const area = turf.area(geojson)
            return area
        },    
        async uploadObstacleFile(file) {
            const formData = new FormData()
            const mapStore = useMaps()
            formData.append('outer_border', file)

            try {
                const response = await postTrackSystemCutoutsFromUpload(formData)

                // Handle not supported file format
                if (response.status === 422) {
                    return response
                }

                // Check for self-intersection
                if (response && response.features && response.features[1] && 
                    response.features[1].properties && 
                    response.features[1].properties.field_geom_type && 
                    response.features[1].properties.field_geom_type === "self_intersection") {
                    return "self_intersection"
                }
                
                if (!response) {
                    throw new Error('Failed to upload file!')
                }

                let tempObstacleWarningType = []
                let obstaclesOutsideFieldCount = 0
                // Process cutouts from response
                if (response.cutout && response.cutout.features) {
                    for (const feature of response.cutout.features) {
                        if (feature.properties && feature.properties.field_geom_type === 'cutout') {
                            const coordinates = feature.geometry.coordinates
                            
                            // Create and add polygon to map
                            const path = geoJsonRingToOpenPath(coordinates[0])

                            const polygon = new mapStore.google.maps.Polygon({
                                id: uuid(),
                                paths: path,
                                editable: true,
                                strokeColor: 'red',
                                strokeWeight: 2,
                                fillColor: 'transparent',
                                fillOpacity: 0,
                                zIndex: 1
                            })

                            // Check if obstacle is not inside field
                            if (!this.isObstacleInsideField(polygon)) {
                                ++obstaclesOutsideFieldCount
                                continue
                            }
                            
                            if (this.isObstacleInsideAnotherObstacle(polygon)) {
                                const overlappingObstacleIndex = this.field.cutouts.findIndex(existingObstacle => 
                                    this.isObstacleInsideAnotherObstacle(polygon, existingObstacle)
                                ) + 1;
                                tempObstacleWarningType = [...tempObstacleWarningType, 'obstacle_overlap_' + overlappingObstacleIndex]
                                continue
                            }

                            // If no conflicts, add to map and array
                            polygon.setMap(mapStore.map)
                            this.field.cutouts.push(polygon)
                            this.addHandleUpdateListeners(polygon)
                            this.surveyCutoutsIDs.push(polygon.id)

                            // Draw SVG after everything is ready
                            setTimeout(async () => {
                                await this.drawObstacle(polygon.id)
                            }, 500)
                        }
                    }
                    // Format: outside_field_<notAddedObstaclesNr>_<totalObstaclesNr> -> example: outside_field_2_5
                    // Format: obstacle_overlap_<obstacleNumber>                      -> example: obstacle_overlap_3
                    if (obstaclesOutsideFieldCount > 0) {
                        tempObstacleWarningType = [...tempObstacleWarningType, 'outside_field_' + obstaclesOutsideFieldCount + '_' + response.cutout.features.length]
                    }
                    this.obstacleWarningType = [...tempObstacleWarningType]
                }

                this.setFieldStyle()
                // add obstacles to context menu
                this.attachContextMenuToObstacles()

                await this.generateHeadlands()
                if (this.field.id) {
                    await this.generateABLines('originalABlines')
                }
                await this.updateGeneratedABlines()
                await this.optimizeLongestEdge()

                await this.handleFieldProblems()

                if (mapStore.terraDraw) {
                    mapStore.terraDraw.setMode('select')
                }

                return { success: true }
            } catch (error) {
                console.error('There was a problem with the fetch operation:', error)
                return { success: false, error: error.message }
            }
        },
        centredMapOnField() {
            const mapStore = useMaps()
            const coordinates = this.field.shapes[0].getPath().getArray().map(coord => [coord.lng(), coord.lat()])
            // Check if the field has more than 2 points
            if (coordinates.length < 2) {
                return
            }
            
            // center map on field if it is the last step or if the field/obstacle is uploaded
            const bounds = new mapStore.google.maps.LatLngBounds()
            coordinates.forEach(coord => {
                bounds.extend(new mapStore.google.maps.LatLng(coord[1], coord[0]))
            })
            mapStore.centerToField({ bounds })
        },
        async generateABLinesByAngle(event) {
            if (event.value >= 0 && event.value <= 360 && event.value !== null) {
                this.angleInput = event.value
                await this.generateABLines('angle')
            } else {
                this.angleInput = null
                this.field.abLines.angle = null
                this.clearABLinesMap()
                this.abLinesOptionsUpdate()
            }
        },
        async handleFieldProblems() {
            const fps = useFieldProblemsStore()

            await fps.checkBorder()
            if (fps.markers.length) {
                await fps.correctBorder(fps.currentPage)
                await fps.seeProblemMarker()
            } else {
                // TESTING
                const success = this.checkFieldValidity()
                if (success !== null) {
                    this.fieldValidationError = true
                    return success
                } else {
                    this.fieldValidationError = false
                }
                // END TESTING
            }
        },
        hasAdminAccess() {
            const userRoleStore = useUserRole()
            return userRoleStore.hasAnyNexatAdminAccess()
        },
        handleFieldTooSmallError(cause) {
            const ss = useSegmentsStore()
            const fps = useFieldProblemsStore()

            const hasProblemMarkers = fps.markers.length > 0

            switch (cause) {
                case 'no_response_from_ab_options_generation':
                    this.clearABLinesMap()
                    this.ablinesGenerated = false
                    // this.headlandGenerated = false

                    // If there are problem markers and the field is too small for AB lines, 
                    // we must focus on the problem markers step, otherwise we focus on the AB lines step
                    this.steps = hasProblemMarkers ? 0 : 2
                    this.abLinesError = 'no_response_from_ab_options_generation'
                    this.field.abLines.longestEdge = null
                    break
                case 'headlands':
                    this.clearABLinesMap()
                    this.ablinesGenerated = false
                    this.headlandGenerated = false

                    // If there are problem markers and the field is too small for headlands, 
                    // we must focus on the problem markers step, otherwise we focus on the headlands step
                    this.steps = hasProblemMarkers ? 0 : 2
                    this.abLinesError = 'fieldTooSmallForHeadlands'
                    // Remove created polygons
                    this.headlandPolygons.forEach(item => {
                        toRaw(item.polygon).setMap(null)
                    })
                    this.headlandPolygons = []
                    this.headlandGenerated = false

                    // Clear all segments
                    ss.clearAll()
                    break
                case 'fieldTooSmallForABLines':
                    this.clearABLinesMap()
                    this.ablinesGenerated = false
                    this.abLinesError = 'fieldTooSmallForABLines'
                    this.field.abLines.longestEdge = null
                    this.field.abLinesOptions = null
                    this.field.selectedABOption = null
                    // If there are problem markers and the field is too small for AB lines generation, 
                    // we must focus on the problem markers step, otherwise we focus on the headlands step
                    this.steps = hasProblemMarkers ? 0 : 2
                    break
            }
        },
        getSnapBorderRings() {
            const rings = [this.field.outerBorder.coordinates[0]]
            this.field.cutouts.forEach(cutout => {
                rings.push(
                    toRaw(cutout).getPath().getArray().map(coord => [coord.lng(), coord.lat()])
                )
            })
            return rings
        },
        snapToBorder({ lat, lng }) {
            const pt = turf.point([lng, lat])
            let closest = null

            this.getSnapBorderRings().forEach(ring => {
                const snapped = turf.nearestPointOnLine(turf.lineString(ring), pt)
                if (!closest || snapped.properties.dist < closest.properties.dist) {
                    closest = snapped
                }
            })

            const [snappedLng, snappedLat] = closest.geometry.coordinates
            return { lat: snappedLat, lng: snappedLng }
        },
        async toggleSelectBorderPoints() {
            if (!this.field.outerBorder || !this.field.outerBorder.coordinates) return

            this.showSelectBorderPoints = !this.showSelectBorderPoints
            if (this.showSelectBorderPoints) {
                if (this.showDrawDirectionLine) {
                    this.clearTempDrawing()
                }
                if (this.showSelectBorder) {
                    await this.toggleSelectBorder()
                }
                this.turnOffEditablePolygon()
                this.initBorderPointsSelection()
                return
            }
            this.removeBorderPointsListener()
            if (!this.showSelectBorder && !this.showDrawDirectionLine) {
                this.turnOnEditablePolygon()
            }
        },
        initBorderPointsSelection() {
            const border = this.field.outerBorder.coordinates[0]
            const midIndex = Math.floor(border.length / 2)
            this.borderPointsStart = { lat: border[0][1], lng: border[0][0] }
            this.borderPointsEnd = { lat: border[midIndex][1], lng: border[midIndex][0] }

            this.borderPointsStartMarker = this.createBorderPointMarker(
                this.borderPointsStart, 'S', '#00FF00', 'start'
            )
            this.borderPointsEndMarker = this.createBorderPointMarker(
                this.borderPointsEnd, 'E', '#FF0000', 'end'
            )
            this.updateBorderPointsLine()
        },
        createBorderPointMarker(position, glyph, color, type) {
            const ms = useMaps()
            const pinElement = new ms.google.maps.marker.PinElement({
                background: color,
                borderColor: '#000000',
                glyphColor: '#FFFFFF',
                scale: 1.2,
                glyphText: glyph,
            })

            const marker = new ms.google.maps.marker.AdvancedMarkerElement({
                position,
                map: toRaw(ms.map),
                content: pinElement,
                gmpClickable: true,
                zIndex: 1000,
            })
            marker.gmpDraggable = true
            this.attachBorderPointDragListener(marker, type)

            return marker
        },
        attachBorderPointDragListener(marker, type) {
            const ms = useMaps()
            const rawMarker = toRaw(marker)

            ms.google.maps.event.addListener(rawMarker, 'dragend', () => {
                const snapped = this.snapToBorder(rawMarker.position)
                rawMarker.position = new ms.google.maps.LatLng(snapped.lat, snapped.lng)
                if (type === 'start') {
                    this.borderPointsStart = snapped
                } else {
                    this.borderPointsEnd = snapped
                }
                this.updateBorderPointsLine()
            })
        },
        updateBorderPointsLine() {
            if (!this.borderPointsStart || !this.borderPointsEnd) return

            const ms = useMaps()
            const path = [this.borderPointsStart, this.borderPointsEnd]

            if (this.borderPointsLine) {
                this.borderPointsLine.setPath(path)
            } else {
                this.borderPointsLine = new ms.google.maps.Polyline({
                    path,
                    geodesic: false,
                    strokeColor: "turquoise",
                    strokeOpacity: 1,
                    strokeWeight: 4,
                    zIndex: 1000,
                    map: toRaw(ms.map),
                })
            }
        },
        removeBorderPointsListener(clearPoints = false) {
            const ms = useMaps()

            if (this.borderPointsStartMarker) {
                const rawMarker = toRaw(this.borderPointsStartMarker)
                ms.google.maps.event.clearListeners(rawMarker, 'dragend')
                rawMarker.map = null
                this.borderPointsStartMarker = null
            }
            if (this.borderPointsEndMarker) {
                const rawMarker = toRaw(this.borderPointsEndMarker)
                ms.google.maps.event.clearListeners(rawMarker, 'dragend')
                rawMarker.map = null
                this.borderPointsEndMarker = null
            }
            if (this.borderPointsLine) {
                toRaw(this.borderPointsLine).setMap(null)
                this.borderPointsLine = null
            }
            // Keep the selected points as the durable reference (like angleInput / coordinatesInput)
            // so regeneration reuses them; only clear on full reset or error.
            if (clearPoints) {
                this.borderPointsStart = null
                this.borderPointsEnd = null
            }
        }
    },
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useDrawingStore, import.meta.hot))
}
